<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted, watch} from 'vue';
import {storeToRefs} from 'pinia';
import ModalOverlay from 'components/ModalOverlay.vue';
import AdminHub from 'components/AdminHub.vue';
import WeeklyRecapPanel from 'components/WeeklyRecapPanel.vue';
import SettingsPanel from 'components/SettingsPanel.vue';
import RollUp from 'components/RollUp.vue';
import { runPanelTrace } from '../util/holo_trace';
import { isMobileRef } from '../util/mobile';

import {useLoginStore, useUserStatsStore, useUserPreferencesStore, useCellHistoryStore, useProofreadingBackendStore, useHelpRequestStore, CellHistoryEntry} from '../store';
import {BADGE_DEFINITIONS, BUILDING_BADGES, EXPLORATION_BADGES, BadgeDefinition, BadgeTrack, statKeyForTrack} from '../widgets/badge_definitions';
import {BADGE_IMAGE_MAP} from '../widgets/badge_images';
import {DEMO_USERS, DEMO_COMMUNITY_EDITS_WEEK, DEMO_COMMUNITY_EDITS_MONTH} from '../data/demo-users';
import {DATASETS, DatasetEntry, SPECIES_ICONS, segLayerName, canonicalDataset, currentSegLayerName, switchToDataset} from '../datasets';
import {useIssueTagStore, type IssueTag} from '../store';
import {EYEWIRE_FLAG} from '../data/countries';
import pyrIcon from '../../static/badges/pyr/pyr-icon.png';

// ── Props ─────────────────────────────────────────────────────────────────────
const props = defineProps<{
  viewUserId?: string | null;
  initialTab?: string | null;
}>();

// ── Stores ────────────────────────────────────────────────────────────────────
const {sessions} = storeToRefs(useLoginStore());
const statsStore  = useUserStatsStore();
const {stats}     = storeToRefs(statsStore);
const prefsStore  = useUserPreferencesStore();
const {prefs}     = storeToRefs(prefsStore);
const historyStore = useCellHistoryStore();
const {cells: cellHistory} = storeToRefs(historyStore);

const backendStore = useProofreadingBackendStore();

// ── Viewing another user's profile ────────────────────────────────────────────
const viewingOtherUser = computed(() => !!props.viewUserId && props.viewUserId !== backendStore.userId);
const otherUserProfile = ref<any>(null);

async function loadOtherUser() {
  if (viewingOtherUser.value && props.viewUserId) {
    otherUserProfile.value = await backendStore.loadUserProfile(props.viewUserId);
  } else {
    otherUserProfile.value = null;
    backendStore.loadUserStats();
  }
  // Load podium counts for whichever user we're showing
  await loadPodium();
}
loadOtherUser();

// ── Weekly podium counts (🥇/🥈/🥉) ─────────────────────────────────
// Two podiums: edits (split+merge wins) and completions (cell-mark wins).
// loadPodium fetches both metrics in parallel; the UI tile is hidden when
// a metric has no podium history.
const podium = ref<{ gold: number; silver: number; bronze: number }>({ gold: 0, silver: 0, bronze: 0 });
const podiumCompletions = ref<{ gold: number; silver: number; bronze: number }>({ gold: 0, silver: 0, bronze: 0 });
async function loadPodium() {
  const id = (viewingOtherUser.value ? props.viewUserId : backendStore.userId) || '';
  if (!id) {
    podium.value = { gold: 0, silver: 0, bronze: 0 };
    podiumCompletions.value = { gold: 0, silver: 0, bronze: 0 };
    return;
  }
  const [edits, completions] = await Promise.all([
    backendStore.loadWeeklyPodium(id, 'edits'),
    backendStore.loadWeeklyPodium(id, 'completions'),
  ]);
  podium.value = edits;
  podiumCompletions.value = completions;
}
const podiumTotal = computed(() => podium.value.gold + podium.value.silver + podium.value.bronze);
const podiumCompletionsTotal = computed(() =>
  podiumCompletions.value.gold + podiumCompletions.value.silver + podiumCompletions.value.bronze);
const anyPodium = computed(() => podiumTotal.value + podiumCompletionsTotal.value > 0);

// Profile display name & email — works for both self and other users
const profileName = computed(() => {
  if (viewingOtherUser.value && otherUserProfile.value) return otherUserProfile.value.display_name || 'Anonymous';
  return backendStore.userName || sessions.value?.[0]?.name || 'Researcher';
});
// NOTE: a `profileEmail` computed used to live here and was rendered on the
// profile. Removed rather than merely hidden — it resolved the email of
// whichever user's profile was open, so clicking a name in chat revealed that
// person's address. The username is the public identifier now.
/** Chat handle. Empty until the user sets one — the row is hidden in that case. */
const profileUsername = computed(() => {
  if (viewingOtherUser.value && otherUserProfile.value) return otherUserProfile.value.username || '';
  return backendStore.username || '';
});
const profileFlag = computed(() => {
  if (viewingOtherUser.value && otherUserProfile.value) return otherUserProfile.value.flag || '';
  return prefs.value.flag || '';
});
const profileStats = computed(() => {
  if (viewingOtherUser.value && otherUserProfile.value) {
    const u = otherUserProfile.value;
    return {
      editsAllTime: u.total_edits || 0, mergesAllTime: u.total_merges || 0, splitsAllTime: u.total_splits || 0,
      cellsSubmitted: u.cells_completed || 0, currentStreak: u.current_streak || 0, longestStreak: u.longest_streak || 0,
      // Weekly/monthly/today not available for other users — show 0
      editsThisWeek: 0, mergesThisWeek: 0, splitsThisWeek: 0,
      editsThisMonth: 0, mergesThisMonth: 0, splitsThisMonth: 0,
      editsToday: 0, mergesToday: 0, splitsToday: 0,
    };
  }
  return stats.value;
});
const profileSpecialBadges = computed(() => {
  if (viewingOtherUser.value && otherUserProfile.value) return otherUserProfile.value.specialBadges || [];
  return backendStore.mySpecialBadges;
});

// Player assists — help requests resolved by current user
const helpStore = useHelpRequestStore();
const playerAssists = computed(() => {
  if (viewingOtherUser.value) return 0;
  const name = backendStore.userName || backendStore.userEmail?.split('@')[0];
  if (!name) return 0;
  return helpStore.requests.filter(r => r.resolved && r.resolvedByName === name).length;
});

// ── Local state ───────────────────────────────────────────────────────────────
const closing        = ref(false);
const selectedBadge  = ref<BadgeDefinition | null>(null);
const selectedSpecialBadge = ref<any | null>(null);
const showFlagPicker = ref(false);
const showAllBuilding    = ref(false);
const showAllExploration = ref(false);
const showAllSpecialModal = ref(false);
const BADGE_PREVIEW_LIMIT = 8;  // 2 rows of 4
const BADGE_PREVIEW_WITH_VIEWALL = 7;  // 7 badges + 1 "View All" tile = 8 slots
const SPECIAL_PREVIEW_LIMIT = 8;

// ── Profile tabs ─────────────────────────────────────────────────────────────
const activeTab = ref<'overview' | 'trophyCase' | 'datasets' | 'weekInScience' | 'adminHub' | 'settings'>('overview');

/** Monday-anchored key for the current week, e.g. "2026-07-13". */
function isoWeekKey(): string {
  const now = new Date();
  const day = now.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(now);
  mon.setDate(now.getDate() + diffToMon);
  mon.setHours(0, 0, 0, 0);
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
}

/** Push the user's weekly recap into their notification feed — once per week.
 *  Deduped via a per-user, per-week localStorage flag so re-opening the tab
 *  doesn't spam. */
async function maybeSendWeeklyRecapNotification() {
  const uid = backendStore.userId;
  if (!uid) return;
  const key = `nge_recap_notif_${uid}_${isoWeekKey()}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, '1'); // set first so a rapid re-open can't double-send
  try {
    const s = stats.value;
    const body = `${s.editsThisWeek.toLocaleString()} edits · ${s.mergesThisWeek.toLocaleString()} merges · ${s.splitsThisWeek.toLocaleString()} splits this week. Open your profile → Week in Science for the full recap.`;
    const { supabase } = await import('../supabase');
    await supabase.from('notifications').insert({
      title: '📊 Your Week in Science',
      body,
      target_type: 'user',
      target_id: uid,
      send_at: new Date().toISOString(),
      created_by: uid,
    });
  } catch (e) {
    console.warn('[profile] weekly recap notification failed:', e);
    localStorage.removeItem(key); // allow a retry next time
  }
}

function openWeekInScience() {
  activeTab.value = 'weekInScience';
  maybeSendWeeklyRecapNotification();
}

// ── Scout Report: your tag activity, all lanes ───────────────────────────────
const issueTagStore = useIssueTagStore();
const myTagsPlaced = computed(() =>
  issueTagStore.tags.filter((t: IssueTag) => t.userId && t.userId === backendStore.userId));
const myTagsFixed = computed(() =>
  myTagsPlaced.value.filter((t: IssueTag) => t.status === 'resolved'));
const tagsIFixed = computed(() =>
  issueTagStore.tags.filter((t: IssueTag) =>
    t.status === 'resolved' && t.resolvedById && t.resolvedById === backendStore.userId));

// The particle trace: an arc of light runs the shell boundary once when the
// profile arrives (scifi-ui hologram.js section 1, via util/holo_trace).
const shellEl = ref<HTMLElement | null>(null);

/**
 * NEVER-CLIPPED WATCHDOG (Amy: "why do I have to ask every day").
 * CSS clamps have failed in the wild more than once (overlay stacking,
 * status bar, specificity fights), so this measures the ACTUAL rendered
 * rects and force-fixes them inline, which no stylesheet can override.
 * If it ever has to intervene it logs the measurements, so the next
 * report comes with data instead of another round of guessing.
 */
function clampProfile() {
  const shell = shellEl.value;
  if (!shell) return;
  const overlay = shell.closest('.overlay-content') as HTMLElement | null;
  const vh = window.innerHeight, vw = window.innerWidth;
  const target = overlay ?? shell;
  const r = target.getBoundingClientRect();
  const clipped = r.bottom > vh - 4 || r.top < 4 || r.right > vw - 4 || r.left < 4;
  if (!clipped) return;
  console.warn('[profile] clip watchdog engaged:', {
    overlay: overlay?.getBoundingClientRect(), shell: shell.getBoundingClientRect(),
    vh, vw, shellMaxH: getComputedStyle(shell).maxHeight,
    overlayTransform: overlay ? getComputedStyle(overlay).transform : null,
  });
  shell.style.maxHeight = (vh - 56) + 'px';
  shell.style.maxWidth = (vw - 48) + 'px';
  if (overlay) {
    overlay.style.maxHeight = (vh - 24) + 'px';
    overlay.style.maxWidth = (vw - 24) + 'px';
    overlay.style.top = '50%';
    overlay.style.left = '50%';
    overlay.style.transform = 'translate(-50%, -50%)';
    overlay.style.overflow = 'auto';
  }
}
let clampTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  // Re-check as the open animation settles, on every resize, and briefly on
  // an interval to catch late layout (fonts, images, tab content).
  requestAnimationFrame(clampProfile);
  setTimeout(clampProfile, 350);
  window.addEventListener('resize', clampProfile);
  clampTimer = setInterval(clampProfile, 700);
  setTimeout(() => { if (clampTimer) { clearInterval(clampTimer); clampTimer = null; } }, 4000);
});
onUnmounted(() => {
  window.removeEventListener('resize', clampProfile);
  if (clampTimer) clearInterval(clampTimer);
});
watch(() => shellEl.value, () => requestAnimationFrame(clampProfile));
watch(() => activeTab.value, () => { requestAnimationFrame(clampProfile); setTimeout(clampProfile, 320); });

onMounted(() => {
  setTimeout(() => { if (shellEl.value) runPanelTrace(shellEl.value); }, 60);
});

// Deep-link: open directly on a given tab (e.g. from the toolbar/command palette
// "Your Week in Science" action).
onMounted(() => {
  if (props.initialTab === 'weekInScience' && !viewingOtherUser.value) {
    openWeekInScience();
  } else if (props.initialTab === 'datasets' && !viewingOtherUser.value) {
    activeTab.value = 'datasets';
  } else if (props.initialTab === 'settings' && !viewingOtherUser.value) {
    activeTab.value = 'settings';
  } else if (props.initialTab === 'triage' && !viewingOtherUser.value) {
    // Deep-link from a 🗂 triage notification: straight to the review queue.
    activeTab.value = 'adminHub';
    adminInitialSubTab.value = 'triage';
  }
});

/** Sub-tab AdminHub should open on; set by the triage deep-link. */
const adminInitialSubTab = ref<string | undefined>(undefined);

// ── Datasets tab: per-dataset contributions + switcher ───────────────────────
// One card per known dataset with this user's contribution counts, doubling as
// the dataset switcher (same switchToDataset the top-bar selector uses).
interface DatasetContribution { edits: number; completions: number; helpRequests: number; }
const datasetStats = ref<Record<string, DatasetContribution>>({});
const datasetStatsLoading = ref(false);
const activeDatasetCanon = ref('');
const switchingDatasetId = ref<string | null>(null);

function refreshActiveDatasetCanon() {
  activeDatasetCanon.value = canonicalDataset(currentSegLayerName());
}

/** All dataset-tag variants that rows for this dataset may carry. edit_log and
 *  help_requests are canonicalised, but cave_completions_mirror stamps the
 *  sync config's own name (e.g. 'pinky_sandbox' where canonical is
 *  'pinky_nf_v2'), so count with .in() across the variants. */
function datasetTagVariants(ds: DatasetEntry): string[] {
  return [...new Set([canonicalDataset(segLayerName(ds)), ds.id, segLayerName(ds)])];
}

async function loadDatasetStats() {
  if (datasetStatsLoading.value) return;
  datasetStatsLoading.value = true;
  refreshActiveDatasetCanon();
  try {
    const uid = backendStore.userId;
    if (!uid) return;
    const { supabase } = await import('../supabase');
    // Numeric CAVE id keys the completions mirror (users.cave_user_id).
    let caveId: number | null = null;
    try {
      const { data } = await supabase.from('users').select('cave_user_id').eq('id', uid).single();
      caveId = data?.cave_user_id ?? null;
    } catch {}
    const out: Record<string, DatasetContribution> = {};
    await Promise.all(DATASETS.map(async ds => {
      const tags = datasetTagVariants(ds);
      const [edits, completions, helpRequests] = await Promise.all([
        supabase.from('edit_log').select('id', { count: 'exact', head: true })
          .eq('user_id', uid).in('dataset', tags).then((r: any) => r.count ?? 0),
        caveId == null ? Promise.resolve(0) :
          supabase.from('cave_completions_mirror').select('segment_id', { count: 'exact', head: true })
            .eq('cave_user_id', caveId).in('dataset', tags).then((r: any) => r.count ?? 0),
        supabase.from('help_requests').select('id', { count: 'exact', head: true })
          .eq('user_id', uid).in('dataset', tags).then((r: any) => r.count ?? 0),
      ]);
      out[canonicalDataset(segLayerName(ds))] = { edits, completions, helpRequests };
    }));
    datasetStats.value = out;
  } catch (e) {
    console.warn('[profile] loadDatasetStats failed:', e);
  } finally {
    datasetStatsLoading.value = false;
  }
}

watch(activeTab, tab => { if (tab === 'datasets') loadDatasetStats(); });

async function switchProfileDataset(ds: DatasetEntry) {
  const canon = canonicalDataset(segLayerName(ds));
  if (canon === activeDatasetCanon.value || switchingDatasetId.value) return;
  switchingDatasetId.value = ds.id;
  const ok = await switchToDataset(ds);
  if (ok) refreshActiveDatasetCanon();
  switchingDatasetId.value = null;
}

function datasetContribution(ds: DatasetEntry): DatasetContribution | undefined {
  return datasetStats.value[canonicalDataset(segLayerName(ds))];
}

function hasContributed(ds: DatasetEntry): boolean {
  const c = datasetContribution(ds);
  return !!c && (c.edits > 0 || c.completions > 0 || c.helpRequests > 0);
}

// ── Inline flag picker ────────────────────────────────────────────────────────
// All country flags A-Z (ISO 3166-1 alpha-2, sorted alphabetically)
const ALL_FLAGS = [
  '🇦🇫','🇦🇱','🇩🇿','🇦🇸','🇦🇩','🇦🇴','🇦🇬','🇦🇷','🇦🇲','🇦🇺',
  '🇦🇹','🇦🇿','🇧🇸','🇧🇭','🇧🇩','🇧🇧','🇧🇾','🇧🇪','🇧🇿','🇧🇯',
  '🇧🇹','🇧🇴','🇧🇦','🇧🇼','🇧🇷','🇧🇳','🇧🇬','🇧🇫','🇧🇮','🇰🇭',
  '🇨🇲','🇨🇦','🇨🇻','🇨🇫','🇹🇩','🇨🇱','🇨🇳','🇨🇴','🇰🇲','🇨🇬',
  '🇨🇩','🇨🇷','🇭🇷','🇨🇺','🇨🇾','🇨🇿','🇩🇰','🇩🇯','🇩🇲','🇩🇴',
  '🇪🇨','🇪🇬','🇸🇻','🇬🇶','🇪🇷','🇪🇪','🇸🇿','🇪🇹','🇫🇯','🇫🇮',
  '🇫🇷','🇬🇦','🇬🇲','🇬🇪','🇩🇪','🇬🇭','🇬🇷','🇬🇩','🇬🇹','🇬🇳',
  '🇬🇼','🇬🇾','🇭🇹','🇭🇳','🇭🇺','🇮🇸','🇮🇳','🇮🇩','🇮🇷','🇮🇶',
  '🇮🇪','🇮🇱','🇮🇹','🇨🇮','🇯🇲','🇯🇵','🇯🇴','🇰🇿','🇰🇪','🇰🇮',
  '🇰🇵','🇰🇷','🇰🇼','🇰🇬','🇱🇦','🇱🇻','🇱🇧','🇱🇸','🇱🇷','🇱🇾',
  '🇱🇮','🇱🇹','🇱🇺','🇲🇬','🇲🇼','🇲🇾','🇲🇻','🇲🇱','🇲🇹','🇲🇭',
  '🇲🇷','🇲🇺','🇲🇽','🇫🇲','🇲🇩','🇲🇨','🇲🇳','🇲🇪','🇲🇦','🇲🇿',
  '🇲🇲','🇳🇦','🇳🇷','🇳🇵','🇳🇱','🇳🇿','🇳🇮','🇳🇪','🇳🇬','🇲🇰',
  '🇳🇴','🇴🇲','🇵🇰','🇵🇼','🇵🇦','🇵🇬','🇵🇾','🇵🇪','🇵🇭','🇵🇱',
  '🇵🇹','🇶🇦','🇷🇴','🇷🇺','🇷🇼','🇰🇳','🇱🇨','🇻🇨','🇼🇸','🇸🇲',
  '🇸🇹','🇸🇦','🇸🇳','🇷🇸','🇸🇨','🇸🇱','🇸🇬','🇸🇰','🇸🇮','🇸🇧',
  '🇸🇴','🇿🇦','🇸🇸','🇪🇸','🇱🇰','🇸🇩','🇸🇷','🇸🇪','🇨🇭','🇸🇾',
  '🇹🇼','🇹🇯','🇹🇿','🇹🇭','🇹🇱','🇹🇬','🇹🇴','🇹🇹','🇹🇳','🇹🇷',
  '🇹🇲','🇹🇻','🇺🇬','🇺🇦','🇦🇪','🇬🇧','🇺🇸','🇺🇾','🇺🇿','🇻🇺',
  '🇻🇪','🇻🇳','🇾🇪','🇿🇲','🇿🇼',
];

/** Convert flag emoji to ISO 3166-1 alpha-2 code (🇺🇸 → 'us'). */
function emojiToCode(emoji: string): string {
  const pts = [...emoji];
  if (pts.length < 2) return '';
  return pts.slice(0, 2).map(c => String.fromCharCode((c.codePointAt(0)! - 0x1F1E6) + 97)).join('');
}

/** Get a flag image URL from an emoji flag. Returns '' for non-flag emoji like 🌐. */
function flagImgUrl(emoji: string): string {
  if (!emoji || emoji === EYEWIRE_FLAG) return '';
  const code = emojiToCode(emoji);
  if (!code || code.length !== 2) return '';
  return `https://flagcdn.com/w40/${code}.png`;
}

function isEyewireFlag(flag: string): boolean {
  return flag === EYEWIRE_FLAG;
}

function setFlag(f: string) {
  prefsStore.save({flag: f});
  showFlagPicker.value = false;
}

// Close the flag picker when clicking anywhere outside it
function handleGlobalClick(e: MouseEvent) {
  const el = document.getElementById('nge-profile-flag-wrap');
  if (el && !el.contains(e.target as Node)) showFlagPicker.value = false;
}
onMounted(()  => document.addEventListener('click', handleGlobalClick, true));
onUnmounted(() => document.removeEventListener('click', handleGlobalClick, true));

// ── Canvas cell-dot visualization ─────────────────────────────────────────────
// ── Demo seeding ─────────────────────────────────────────────────────────────
onMounted(() => {
  if (stats.value.editsAllTime === 0) {
    const amy = DEMO_USERS[0];
    statsStore.setStats({
      editsAllTime:            amy.stats.editsAllTime,
      mergesAllTime:           amy.stats.mergesAllTime,
      splitsAllTime:           amy.stats.splitsAllTime,
      editsThisWeek:           amy.stats.editsThisWeek,
      mergesThisWeek:          amy.stats.mergesThisWeek,
      splitsThisWeek:          amy.stats.splitsThisWeek,
      editsThisMonth:          amy.stats.editsThisMonth,
      mergesThisMonth:         amy.stats.mergesThisMonth,
      splitsThisMonth:         amy.stats.splitsThisMonth,
      cellsSubmitted:          amy.stats.cellsSubmitted,
      currentStreak:           amy.stats.currentStreak,
      longestStreak:           amy.stats.longestStreak,
      communityEditsThisWeek:  DEMO_COMMUNITY_EDITS_WEEK,
      communityEditsThisMonth: DEMO_COMMUNITY_EDITS_MONTH,
    });
  }
});

// ── Badge helpers ─────────────────────────────────────────────────────────────
function getBadgeUrl(imageKey: string): string {
  return BADGE_IMAGE_MAP[imageKey] ?? '';
}

/** Get the player's current count for a given track. */
function statForTrack(track: BadgeTrack): number {
  const s = profileStats.value;
  return track === 'building'
    ? (s.editsAllTime ?? 0)
    : (s.cellsSubmitted ?? 0);
}

function isBadgeEarned(badge: BadgeDefinition): boolean {
  if (badge.threshold === 0) return false;
  if (statForTrack(badge.track) >= badge.threshold) return true;
  // Persisted earn: once crossed, a badge stays earned even if the stat later
  // drops. myBadgeAwards is the CURRENT user's, so only apply it to own profile.
  if (!viewingOtherUser.value && badge.id != null &&
      backendStore.myBadgeAwards.has(`${badge.track}:${badge.id}`)) return true;
  return false;
}

function onBadgeClick(badge: BadgeDefinition) {
  if (!isBadgeEarned(badge)) return;
  selectedSpecialBadge.value = null;
  // Toggle: click same badge again to dismiss detail
  selectedBadge.value = selectedBadge.value?.id === badge.id ? null : badge;
}

function onSpecialBadgeClick(award: any) {
  selectedBadge.value = null;
  selectedSpecialBadge.value = selectedSpecialBadge.value?.id === award.id ? null : award;
}

// ── Earned badges + 1 upcoming "next" badge per track ────────────────────────
const earnedBuildingBadges = computed(() => {
  const earned = BUILDING_BADGES.filter(b => isBadgeEarned(b)).reverse();
  const nextLocked = BUILDING_BADGES.find(b => !isBadgeEarned(b));
  return { earned, next: nextLocked ?? null };
});

const earnedExplorationBadges = computed(() => {
  const earned = EXPLORATION_BADGES.filter(b => isBadgeEarned(b)).reverse();
  const nextLocked = EXPLORATION_BADGES.find(b => !isBadgeEarned(b));
  return { earned, next: nextLocked ?? null };
});

/** Most recently earned badge (highest threshold among earned). */
const latestEarnedBadge = computed(() => {
  const allEarned = BADGE_DEFINITIONS.filter(b => isBadgeEarned(b));
  if (allEarned.length === 0) return null;
  return allEarned.reduce((a, b) => a.threshold > b.threshold ? a : b);
});

// ── Favorite badge (persisted in localStorage) ────────────────────────────────
/** Favorite badge slug — synced to Supabase via store. */
const favoriteBadgeSlug = computed(() => backendStore.favoriteBadgeSlug);
const favoriteBadge = computed<BadgeDefinition | null>(() => {
  if (favoriteBadgeSlug.value) {
    const found = BADGE_DEFINITIONS.find(b => b.slug === favoriteBadgeSlug.value);
    if (found && isBadgeEarned(found)) return found;
  }
  return null;
});
/** Check if the favorite is a special badge (not a regular track badge). */
const favoriteSpecialBadge = computed(() => {
  if (!favoriteBadgeSlug.value || favoriteBadge.value) return null;
  return profileSpecialBadges.value.find(
    (a: any) => (a.badge?.slug || `special-${a.id}`) === favoriteBadgeSlug.value
  ) || null;
});
/** Badge shown in the Trophy Case featured banner. */
const featuredBadge = computed(() => selectedBadge.value ?? favoriteBadge.value ?? latestEarnedBadge.value);

/** Mobile: tapping the featured badge art opens it fullscreen with a
 *  simplified award animation (Amy 2026-08-18: the banner cannot do the
 *  badge justice on a phone). Desktop taps are inert. */
const badgeZoom = ref<{ img: string; name: string; desc?: string; meta?: string } | null>(null);
function zoomBadge(img?: string | null, name?: string | null, desc?: string | null, meta?: string | null) {
  if (!isMobileRef.value || !img) return;
  badgeZoom.value = { img, name: name || 'Award', desc: desc || undefined, meta: meta || undefined };
}
function toggleFavoriteBadge(badge: BadgeDefinition) {
  const newSlug = favoriteBadgeSlug.value === badge.slug ? '' : badge.slug;
  backendStore.saveFavoriteBadge(newSlug);
}
function toggleFavoriteSpecialBadge(award: any) {
  const slug = award.badge?.slug || `special-${award.id}`;
  const newSlug = favoriteBadgeSlug.value === slug ? '' : slug;
  backendStore.saveFavoriteBadge(newSlug);
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const displayedBuildingBadges = computed(() => {
  const all = earnedBuildingBadges.value.earned;
  if (showAllBuilding.value) return all;
  // When earned >= 8: show 7 + "View All" tile (8 total slots).
  return all.length >= BADGE_PREVIEW_LIMIT ? all.slice(0, BADGE_PREVIEW_WITH_VIEWALL) : all.slice(0, BADGE_PREVIEW_LIMIT);
});
const displayedExplorationBadges = computed(() => {
  const all = earnedExplorationBadges.value.earned;
  if (showAllExploration.value) return all;
  return all.length >= BADGE_PREVIEW_LIMIT ? all.slice(0, BADGE_PREVIEW_WITH_VIEWALL) : all.slice(0, BADGE_PREVIEW_LIMIT);
});
const showBuildingViewAll = computed(() => earnedBuildingBadges.value.earned.length >= BADGE_PREVIEW_LIMIT);
const showExplorationViewAll = computed(() => earnedExplorationBadges.value.earned.length >= BADGE_PREVIEW_LIMIT);

// ── Special Awards "View all" preview ───────────────────────────────────────
const displayedSpecialBadges = computed(() => {
  const all = profileSpecialBadges.value;
  if (all.length > SPECIAL_PREVIEW_LIMIT) return all.slice(0, SPECIAL_PREVIEW_LIMIT - 1); // leave one slot for tile
  return all.slice(0, SPECIAL_PREVIEW_LIMIT);
});
const showSpecialViewAll = computed(() => profileSpecialBadges.value.length > SPECIAL_PREVIEW_LIMIT);

// ── Tooltip helpers for badges ───────────────────────────────────────────────
function badgeTooltip(badge: BadgeDefinition): string {
  const noun = badge.track === 'building' ? 'edits' : 'cells completed';
  return `${badge.name} — Earned for ${badge.threshold.toLocaleString()} ${noun}`;
}
function specialBadgeTooltip(award: any): string {
  const name = award.badge?.name || 'Award';
  const reason = award.reason ? String(award.reason).trim() : '';
  if (reason) return `${name} — ${reason}`;
  if (award.badge?.description) return `${name} — ${award.badge.description}`;
  return `${name} — special award`;
}

/** Label for the threshold in badge detail. */
function thresholdLabel(badge: BadgeDefinition): string {
  return badge.track === 'building' ? 'edits' : 'cells completed';
}

// ── Per-track achievement countdowns ─────────────────────────────────────────
function nextForTrack(badges: BadgeDefinition[], current: number) {
  const sorted = [...badges].filter(b => b.threshold > 0).sort((a, b) => a.threshold - b.threshold);
  const next = sorted.find(b => current < b.threshold);
  if (!next) return null;
  const prev = sorted[sorted.indexOf(next) - 1]?.threshold ?? 0;
  const remaining = next.threshold - current;
  const pct = Math.min(100, Math.round(((current - prev) / (next.threshold - prev)) * 100));
  return { name: next.name, threshold: next.threshold, remaining, pct, track: next.track as BadgeTrack };
}

const nextBuildingAchievement = computed(() =>
  nextForTrack(BUILDING_BADGES, profileStats.value.editsAllTime ?? 0)
);
const nextExplorationAchievement = computed(() =>
  nextForTrack(EXPLORATION_BADGES, profileStats.value.cellsSubmitted ?? 0)
);

// ── Current dataset helper (for filtering cells) ────────────────────────────
function getCurrentDataset(): string {
  try {
    const viewer = (window as any)['viewer'];
    for (const ml of viewer?.layerManager?.managedLayers ?? []) {
      // Check layer type name (works even if dataSources haven't loaded)
      const typeName = ml.layer?.constructor?.name ?? '';
      if (typeName.includes('Segmentation')) return ml.name ?? '';
      // Fallback: check URL
      const url = ml.layer?.dataSources?.[0]?.spec?.url ?? '';
      if (url.includes('graphene') || url.includes('segmentation')) return ml.name ?? '';
    }
  } catch {}
  return '';
}
const currentDataset = ref('');
onMounted(() => { currentDataset.value = getCurrentDataset(); });

// ── Cell history helpers ──────────────────────────────────────────────────────
/** Filter cells by current dataset (if set). Cells without a dataset tag always show. */
const filteredCellHistory = computed(() => {
  const ds = currentDataset.value;
  if (!ds) return cellHistory.value;
  return cellHistory.value.filter(c => !c.dataset || c.dataset === ds);
});

const completedCells = computed(() => filteredCellHistory.value.filter(c => c.isComplete));
const identifiedCells = computed(() => filteredCellHistory.value.filter(c => c.cellType && !c.isComplete));

// ── 14-day activity chart data ───────────────────────────────────────────────
const last14Days = computed(() => {
  const days: Array<{ date: string; label: string; edits: number; completions: number; total: number }> = [];
  const log = statsStore.dailyLog;
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const entry = log.find((e: {date: string}) => e.date === dateStr);
    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'narrow' });
    days.push({
      date: dateStr,
      label: i === 0 ? 'Today' : dayLabel,
      edits: entry?.edits ?? 0,
      completions: entry?.cellsCompleted ?? 0,
      total: (entry?.edits ?? 0) + (entry?.cellsCompleted ?? 0),
    });
  }
  return days;
});

const chartMax = computed(() => Math.max(1, ...last14Days.value.map(d => d.total)));

function truncateId(id: string): string {
  return id.length > 12 ? id.slice(0, 5) + '…' + id.slice(-5) : id;
}

function cellStatusIcon(cell: CellHistoryEntry): string {
  if (cell.isComplete && cell.cellType) return '✓';
  if (cell.isComplete) return '✓';
  if (cell.cellType) return '🏷';
  return '○';
}

function cellStatusClass(cell: CellHistoryEntry): string {
  if (cell.isComplete) return 'nge-cell-status--complete';
  if (cell.cellType) return 'nge-cell-status--typed';
  return 'nge-cell-status--pending';
}

function handleJumpToCell(segId: string) {
  historyStore.jumpToCell(segId);
  handleClose();
}

function promptNickname(segId: string) {
  const cell = cellHistory.value.find(c => c.segId === segId);
  const current = cell?.nickname ?? '';
  const name = window.prompt('Name this cell:', current);
  if (name !== null) {
    historyStore.setNickname(segId, name);
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Close animation ───────────────────────────────────────────────────────────
function handleClose() {
  if (closing.value) return;
  closing.value = true;
  setTimeout(() => emit('hide'), 165);
}

const emit = defineEmits({hide: null, 'open-settings': null});
</script>

<template>
  <modal-overlay
    id="nge-profile-modal"
    class="nge-profile-modal"
    :class="{ 'nge-profile-closing': closing }"
    @hide="handleClose"
  >
    <div ref="shellEl" class="nge-profile-shell" :class="{ 'nge-profile-shell--trophy': activeTab === 'trophyCase', 'nge-profile-shell--admin': activeTab === 'adminHub', 'nge-profile-shell--week': activeTab === 'weekInScience', 'nge-profile-shell--datasets': activeTab === 'datasets', 'nge-profile-shell--settings': activeTab === 'settings' }">

      <!-- ── Topbar ─────────────────────────────────────────── -->
      <div class="nge-profile-topbar">
        <span class="nge-profile-topbar-label">◈ <span class="nge-profile-topbar-name">{{ profileUsername || profileName }}</span> · RESEARCHER PROFILE</span>
        <button class="nge-profile-exit" @click="handleClose">×</button>
      </div>

      <!-- ── Tab bar ─────────────────────────────────────────── -->
      <div class="nge-profile-tabbar">
        <button
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': activeTab === 'overview' }"
          @click="activeTab = 'overview'"
        >Overview</button>
        <button
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': activeTab === 'trophyCase' }"
          @click="activeTab = 'trophyCase'"
        >🏆 Trophy Case</button>
        <button
          v-if="!viewingOtherUser"
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': activeTab === 'datasets' }"
          @click="activeTab = 'datasets'"
        >🧬 Datasets</button>
        <!-- Hidden by default (Amy 2026-08-11): the tab button only appears
             while you're ON it, i.e. arrived via the toolbar Week icon or a
             recap notification deep-link. -->
        <button
          v-if="!viewingOtherUser && activeTab === 'weekInScience'"
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': true }"
          @click="openWeekInScience()"
        >📊 My Week in Science</button>
        <button
          v-if="!viewingOtherUser"
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': activeTab === 'settings' }"
          @click="activeTab = 'settings'"
        >⚙️ Settings</button>
        <button
          v-if="!viewingOtherUser && backendStore.isAdmin"
          class="nge-profile-tab"
          :class="{ 'nge-profile-tab--active': activeTab === 'adminHub' }"
          @click="activeTab = 'adminHub'"
        >🛠 Admin Hub</button>
      </div>

      <!-- ── Three-column body (Overview tab) ────────────────── -->
      <div v-if="activeTab === 'overview'" class="nge-profile-body">

        <!-- LEFT: stats + recent cells -->
        <div class="nge-profile-col nge-profile-col--left">

          <!-- Header -->
          <div class="nge-profile-header" v-if="sessions.length > 0 || viewingOtherUser">
            <div class="nge-profile-name-row">

              <!-- Flag (editable only for own profile) -->
              <div v-if="!viewingOtherUser" id="nge-profile-flag-wrap" class="nge-profile-flag-wrap" @click.stop>
                <button
                  class="nge-profile-flag"
                  :class="{ 'nge-profile-flag--active': showFlagPicker }"
                  @click="showFlagPicker = !showFlagPicker"
                  title="Click to change flag"
                >
                  <img v-if="isEyewireFlag(profileFlag)" :src="pyrIcon" class="nge-flag-img nge-pyr-icon" />
                  <img v-else-if="flagImgUrl(profileFlag)" :src="flagImgUrl(profileFlag)" class="nge-flag-img" />
                  <img v-else :src="pyrIcon" class="nge-flag-img nge-pyr-icon" />
                </button>
                <Transition name="nge-flag-picker">
                  <div v-if="showFlagPicker" class="nge-profile-flag-picker">
                    <button
                      v-for="f in ALL_FLAGS" :key="f"
                      class="nge-profile-flag-opt"
                      :class="{ 'nge-profile-flag-opt--active': prefs.flag === f }"
                      @click="setFlag(f)"
                    ><img :src="flagImgUrl(f)" class="nge-flag-img nge-flag-img--picker" /></button>
                  </div>
                </Transition>
              </div>
              <div v-else class="nge-profile-flag-wrap">
                <img v-if="isEyewireFlag(profileFlag)" :src="pyrIcon" class="nge-flag-img nge-pyr-icon" style="width:24px;height:24px;" />
                <img v-else-if="flagImgUrl(profileFlag)" :src="flagImgUrl(profileFlag)" class="nge-flag-img" style="width:28px;height:20px;" />
              </div>

              <div class="nge-profile-name">{{ profileName }}</div>

              <button v-if="!viewingOtherUser" class="nge-profile-edit-btn"
                      @click="activeTab = 'settings'"
                      title="Edit Profile — set bio, flag, and more">⚙</button>
            </div>

            <!-- The handle people actually use to tag you in chat, so it
                 belongs on the profile next to the name. -->
            <div v-if="profileUsername" class="nge-profile-username">@{{ profileUsername }}</div>

            <!-- Email is deliberately NOT shown. It was rendered for whoever's
                 profile was open, which exposed other users' addresses to
                 anyone who clicked their name in chat. The username is the
                 public identifier; the address stays private. -->


            <template v-if="!viewingOtherUser">
              <div class="nge-profile-bio" v-if="prefs.bio">{{ prefs.bio }}</div>
              <button v-else class="nge-profile-bio-add" @click="activeTab = 'settings'">
                + Add a bio
              </button>
            </template>
          </div>

          <!-- Edits stats -->
          <div class="nge-profile-section nge-profile-section--edits">
            <div class="nge-profile-section-label">▌ Edits</div>
            <div class="nge-profile-stat-row">
              <div class="nge-profile-stat-col" v-for="(col, i) in [
                {label:'Today',    val:profileStats.editsToday,    merges:profileStats.mergesToday,    splits:profileStats.splitsToday},
                {label:'Past 7d',  val:profileStats.editsThisWeek, merges:profileStats.mergesThisWeek, splits:profileStats.splitsThisWeek},
                {label:'All Time', val:profileStats.editsAllTime,  merges:profileStats.mergesAllTime,  splits:profileStats.splitsAllTime},
              ]" :key="i">
                <div class="nge-profile-stat-label">{{ col.label }}</div>
                <div class="nge-profile-stat-val"><RollUp :value="col.val" /></div>
                <div class="nge-profile-stat-breakdown">
                  <span class="nge-profile-stat-bp" title="Merges">
                    <svg class="nge-profile-stat-icon nge-profile-stat-icon--merge" viewBox="0 0 16 16" fill="none">
                      <path d="M4 3v4a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                      <circle cx="4" cy="2.5" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="2.5" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                      <path d="M8 11v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                    {{ col.merges }}
                  </span>
                  <span class="nge-profile-stat-bp" title="Splits">
                    <svg class="nge-profile-stat-icon nge-profile-stat-icon--split" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3v2a4 4 0 0 1-4 4H4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                      <path d="M8 3v2a4 4 0 0 0 4 4h0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                      <circle cx="8" cy="2.5" r="1.5" fill="currentColor"/>
                      <circle cx="4" cy="13" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="13" r="1.5" fill="currentColor"/>
                      <path d="M4 9v4M12 9v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                    {{ col.splits }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Cells stats -->
          <div v-if="!viewingOtherUser" class="nge-profile-section nge-profile-section--cells">
            <div class="nge-profile-section-label">▌ Cells</div>
            <!-- Sub-label row removed (Amy): tooltips carry the definitions,
                 and each stat sits in its own highlighted tile. -->
            <div class="nge-profile-stat-row nge-profile-stat-row--tiles">
              <div class="nge-profile-stat-col nge-profile-stat-tile" title="Cells you proofread all the way to completion">
                <div class="nge-profile-stat-label">Completed</div>
                <div class="nge-profile-stat-val nge-profile-stat-val--hero">
                  <RollUp :value="completedCells.length" />
                </div>
              </div>
              <div class="nge-profile-stat-col nge-profile-stat-tile" title="Cells you identified (typed) without proofreading edits">
                <div class="nge-profile-stat-label">Identified</div>
                <div class="nge-profile-stat-val"><RollUp :value="identifiedCells.length" /></div>
              </div>
              <div class="nge-profile-stat-col nge-profile-stat-tile" title="Every cell you have worked on in any way">
                <div class="nge-profile-stat-label">Total</div>
                <div class="nge-profile-stat-val"><RollUp :value="filteredCellHistory.length" /></div>
              </div>
              <div class="nge-profile-stat-col nge-profile-stat-tile" v-if="playerAssists > 0" title="Cells where you helped answer another player's request">
                <div class="nge-profile-stat-label">Assists</div>
                <div class="nge-profile-stat-val" style="color: #7f8;">{{ playerAssists }}</div>
              </div>
            </div>

            <!-- Scout Report: tags placed and fixed -->
            <div class="nge-profile-section-label" style="margin-top: 14px;">▌ Scout Report</div>
            <div class="nge-profile-stat-row">
              <div class="nge-profile-stat-col">
                <div class="nge-profile-stat-label">Tags Placed</div>
                <div class="nge-profile-stat-val" style="color: #f5d142;"><RollUp :value="myTagsPlaced.length" /></div>
                <div class="nge-profile-stat-sub">scouted</div>
              </div>
              <div class="nge-profile-stat-col">
                <div class="nge-profile-stat-label">Confirmed</div>
                <div class="nge-profile-stat-val"><RollUp :value="myTagsFixed.length" /></div>
                <div class="nge-profile-stat-sub">of yours, fixed</div>
              </div>
              <div class="nge-profile-stat-col">
                <div class="nge-profile-stat-label">You Fixed</div>
                <div class="nge-profile-stat-val" style="color: #9d9;"><RollUp :value="tagsIFixed.length" /></div>
                <div class="nge-profile-stat-sub">scythe work</div>
              </div>
            </div>

            <!-- Cell history list -->
            <div class="nge-cell-list" v-if="filteredCellHistory.length > 0">
              <div class="nge-cell-list-header">
                <span class="nge-cell-list-title">Recent Cells</span>
                <span v-if="currentDataset" class="nge-cell-list-dataset" :title="'Filtered to ' + currentDataset">{{ currentDataset }}</span>
              </div>
              <div class="nge-cell-list-columns">
                <span class="nge-cell-col-label nge-cell-col-label--id">Segment</span>
                <span class="nge-cell-col-label nge-cell-col-label--type">Type</span>
                <span class="nge-cell-col-label nge-cell-col-label--time">When</span>
              </div>
              <div class="nge-cell-list-scroll">
                <div
                  v-for="cell in filteredCellHistory.slice(0, 50)"
                  :key="cell.segId"
                  class="nge-cell-row"
                  :title="cell.nickname ? `${cell.nickname} (${cell.segId})` : `Jump to ${cell.segId}`"
                >
                  <button
                    class="nge-cell-fav"
                    :class="{ 'nge-cell-fav--active': cell.isFavorite }"
                    @click.stop="historyStore.toggleFavorite(cell.segId)"
                    title="Toggle favorite"
                  >{{ cell.isFavorite ? '★' : '☆' }}</button>
                  <span class="nge-cell-pip" :class="cellStatusClass(cell)">{{ cellStatusIcon(cell) }}</span>
                  <span class="nge-cell-id" @click="handleJumpToCell(cell.segId)">
                    {{ cell.nickname || truncateId(cell.segId) }}
                  </span>
                  <span class="nge-cell-type" v-if="cell.cellType" @click="handleJumpToCell(cell.segId)">{{ cell.cellType }}</span>
                  <span class="nge-cell-time" @click="handleJumpToCell(cell.segId)">{{ relativeTime(cell.updatedAt) }}</span>
                  <button
                    class="nge-cell-rename"
                    @click.stop="promptNickname(cell.segId)"
                    title="Rename this cell"
                  >✎</button>
                </div>
              </div>
            </div>
            <div class="nge-cell-empty" v-else>
              Select segments and mark complete or set cell type to build your history.
            </div>
          </div>

        </div><!-- end left column -->

        <!-- CENTER: countdown + badges + streak -->
        <div class="nge-profile-col nge-profile-col--center">

          <!-- Proofreading Achievements (building track) -->
          <div class="nge-profile-section nge-profile-section--badges">
            <div class="nge-profile-section-label" style="color: #ffd08a;">▌ Proofreading Achievements</div>
            <div class="nge-profile-countdown-inline" v-if="nextBuildingAchievement">
              <div class="nge-profile-countdown-row">
                <div class="nge-profile-countdown-remaining">{{ nextBuildingAchievement.remaining.toLocaleString() }} edits to go</div>
              </div>
              <div class="nge-profile-countdown-track">
                <div class="nge-profile-countdown-fill nge-profile-countdown-fill--building" :style="{ width: nextBuildingAchievement.pct + '%' }"></div>
              </div>
            </div>
            <div class="nge-profile-badges-grid">
              <div
                v-for="badge in displayedBuildingBadges"
                :key="badge.id"
                class="nge-profile-badge nge-profile-badge--building"
                :class="{
                  'nge-profile-badge--selected': selectedBadge?.id === badge.id,
                  'nge-profile-badge--latest': latestEarnedBadge?.id === badge.id,
                }"
                :title="badgeTooltip(badge)"
                @click="onBadgeClick(badge)"
              >
                <div class="nge-profile-badge-img">
                  <img :src="getBadgeUrl(badge.imageKey)" :alt="badge.name" class="nge-profile-badge-icon" :class="`nge-badge--${badge.slug}`" />
                </div>
                <div class="nge-profile-badge-name">{{ badge.name }}</div>
              </div>
              <!-- Next locked badge teaser -->
              <div
                v-if="earnedBuildingBadges.next && !showBuildingViewAll"
                class="nge-profile-badge nge-profile-badge--locked"
                :title="'Next: keep editing to unlock!'"
              >
                <div class="nge-profile-badge-img">
                  <div class="nge-profile-badge-mystery">
                    <span class="nge-profile-badge-mystery-q">?</span>
                  </div>
                </div>
              </div>
              <!-- "View All" tile at end of row 2 -->
              <div
                v-if="showBuildingViewAll && !showAllBuilding"
                class="nge-profile-badge nge-profile-badge--viewall"
                title="View all badges in Trophy Case"
                @click="activeTab = 'trophyCase'"
              >
                <div class="nge-profile-badge-img">
                  <div class="nge-profile-badge-viewall-icon">→</div>
                </div>
                <div class="nge-profile-badge-name">View all</div>
              </div>
            </div>
            <div v-if="earnedBuildingBadges.earned.length === 0" class="nge-profile-badges-empty">
              Make your first edit to earn a badge!
            </div>
          </div>

          <!-- Divider between achievement tracks -->
          <div class="nge-profile-badges-divider"></div>

          <!-- Cell Achievements (exploration track) -->
          <div class="nge-profile-section nge-profile-section--badges">
            <div class="nge-profile-section-label" style="color: #90fff2;">▌ Cell Achievements</div>
            <div class="nge-profile-countdown-inline" v-if="nextExplorationAchievement">
              <div class="nge-profile-countdown-row">
                <div class="nge-profile-countdown-remaining">{{ nextExplorationAchievement.remaining.toLocaleString() }} cells to go</div>
              </div>
              <div class="nge-profile-countdown-track">
                <div class="nge-profile-countdown-fill nge-profile-countdown-fill--exploration" :style="{ width: nextExplorationAchievement.pct + '%' }"></div>
              </div>
            </div>
            <div class="nge-profile-badges-grid">
              <div
                v-for="badge in displayedExplorationBadges"
                :key="badge.id"
                class="nge-profile-badge nge-profile-badge--exploration"
                :class="{
                  'nge-profile-badge--selected': selectedBadge?.id === badge.id,
                  'nge-profile-badge--latest': latestEarnedBadge?.id === badge.id,
                }"
                :title="badgeTooltip(badge)"
                @click="onBadgeClick(badge)"
              >
                <div class="nge-profile-badge-img">
                  <img :src="getBadgeUrl(badge.imageKey)" :alt="badge.name" class="nge-profile-badge-icon" :class="`nge-badge--${badge.slug}`" />
                </div>
                <div class="nge-profile-badge-name">{{ badge.name }}</div>
              </div>
              <!-- Next locked badge teaser -->
              <div
                v-if="earnedExplorationBadges.next && !showExplorationViewAll"
                class="nge-profile-badge nge-profile-badge--locked"
                :title="'Next: complete more cells to unlock!'"
              >
                <div class="nge-profile-badge-img">
                  <div class="nge-profile-badge-mystery">
                    <span class="nge-profile-badge-mystery-q">?</span>
                  </div>
                </div>
              </div>
              <!-- "View All" tile at end of row 2 -->
              <div
                v-if="showExplorationViewAll && !showAllExploration"
                class="nge-profile-badge nge-profile-badge--viewall"
                title="View all badges in Trophy Case"
                @click="activeTab = 'trophyCase'"
              >
                <div class="nge-profile-badge-img">
                  <div class="nge-profile-badge-viewall-icon">→</div>
                </div>
                <div class="nge-profile-badge-name">View all</div>
              </div>
            </div>
            <div v-if="earnedExplorationBadges.earned.length === 0" class="nge-profile-badges-empty">
              Complete your first cell to earn a badge!
            </div>
          </div>

          <!-- Special Awards (admin-awarded badges) -->
          <template v-if="profileSpecialBadges.length > 0">
            <div class="nge-profile-badges-divider"></div>
            <div class="nge-profile-section nge-profile-section--badges nge-profile-section--special">
              <div class="nge-profile-section-label">★ Special Awards</div>
              <div class="nge-profile-badges-grid">
                <div
                  v-for="award in displayedSpecialBadges"
                  :key="award.id"
                  class="nge-profile-badge"
                  :class="{ 'nge-profile-badge--selected': selectedSpecialBadge?.id === award.id }"
                  :title="specialBadgeTooltip(award)"
                  @click="onSpecialBadgeClick(award)"
                >
                  <div class="nge-profile-badge-img">
                    <img :src="award.badge?.thumbnail_url || award.badge?.image_url" :alt="award.badge?.name" class="nge-profile-badge-icon" />
                  </div>
                  <div class="nge-profile-badge-name">{{ award.badge?.name || 'Award' }}</div>
                </div>
                <!-- View All tile when overflow -->
                <div
                  v-if="showSpecialViewAll"
                  class="nge-profile-badge nge-profile-badge--viewall"
                  title="View all special awards"
                  @click="showAllSpecialModal = true"
                >
                  <div class="nge-profile-badge-img">
                    <div class="nge-profile-badge-viewall-icon">→</div>
                  </div>
                  <div class="nge-profile-badge-name">View all</div>
                </div>
              </div>
            </div>
          </template>

        </div><!-- end center column -->

        <!-- RIGHT: latest badge highlight  <->  badge detail -->
        <div class="nge-profile-col nge-profile-col--right">
          <Transition name="nge-viz-swap" mode="out-in">

            <!-- Special badge detail view -->
            <div v-if="selectedSpecialBadge" key="special" class="nge-profile-viz-panel nge-profile-viz-badge nge-profile-viz-badge--detail">
              <img
                :src="selectedSpecialBadge.badge?.image_url || selectedSpecialBadge.badge?.thumbnail_url"
                :alt="selectedSpecialBadge.badge?.name"
                class="nge-profile-viz-badge-icon nge-profile-viz-badge-icon--large"
              />
              <div class="nge-profile-viz-badge-name">{{ selectedSpecialBadge.badge?.name || 'Award' }}</div>
              <div v-if="selectedSpecialBadge.badge?.description" class="nge-profile-viz-badge-desc">{{ selectedSpecialBadge.badge.description }}</div>
              <div class="nge-profile-viz-badge-threshold">
                Awarded {{ relativeTime(selectedSpecialBadge.awarded_at) }}
              </div>
              <button class="nge-profile-viz-badge-back" @click="selectedSpecialBadge = null">
                ← Back
              </button>
            </div>

            <!-- Default view: favorite badge → favorite special badge → latest earned -->
            <div v-else-if="!selectedBadge" key="latest" class="nge-profile-viz-panel nge-profile-viz-badge">
              <template v-if="favoriteBadge">
                <div class="nge-profile-viz-title">⭐ Favorite Badge</div>
                <img
                  :src="getBadgeUrl(favoriteBadge.imageKey)"
                  :alt="favoriteBadge.name"
                  class="nge-profile-viz-badge-icon"
                  :class="`nge-badge--${favoriteBadge.slug}`"
                />
                <div class="nge-profile-viz-badge-name">{{ favoriteBadge.name }}</div>
                <div class="nge-profile-viz-badge-desc">{{ favoriteBadge.description }}</div>
                <div class="nge-profile-viz-badge-threshold">
                  Unlocked at <strong>{{ favoriteBadge.threshold.toLocaleString() }}</strong> {{ thresholdLabel(favoriteBadge) }}
                </div>
              </template>
              <template v-else-if="favoriteSpecialBadge">
                <div class="nge-profile-viz-title">⭐ Favorite Badge</div>
                <img
                  :src="favoriteSpecialBadge.badge?.image_url || favoriteSpecialBadge.badge?.thumbnail_url"
                  :alt="favoriteSpecialBadge.badge?.name"
                  class="nge-profile-viz-badge-icon nge-profile-viz-badge-icon--large"
                />
                <div class="nge-profile-viz-badge-name">{{ favoriteSpecialBadge.badge?.name || 'Award' }}</div>
                <div v-if="favoriteSpecialBadge.badge?.description" class="nge-profile-viz-badge-desc">{{ favoriteSpecialBadge.badge.description }}</div>
                <div class="nge-profile-viz-badge-threshold">
                  Won on <strong>{{ formatDate(favoriteSpecialBadge.awarded_at) }}</strong>
                </div>
              </template>
              <template v-else-if="latestEarnedBadge">
                <div class="nge-profile-viz-title">▌ Latest Badge</div>
                <img
                  :src="getBadgeUrl(latestEarnedBadge.imageKey)"
                  :alt="latestEarnedBadge.name"
                  class="nge-profile-viz-badge-icon"
                  :class="`nge-badge--${latestEarnedBadge.slug}`"
                />
                <div class="nge-profile-viz-badge-name">{{ latestEarnedBadge.name }}</div>
                <div class="nge-profile-viz-badge-desc">{{ latestEarnedBadge.description }}</div>
                <div class="nge-profile-viz-badge-threshold">
                  Earned at <strong>{{ latestEarnedBadge.threshold.toLocaleString() }}</strong> {{ thresholdLabel(latestEarnedBadge) }}
                </div>
              </template>
              <template v-else>
                <div class="nge-profile-viz-title">▌ Badges</div>
                <div class="nge-profile-viz-badge-desc" style="margin-top: 40px;">
                  Make edits and complete cells to earn badges!
                </div>
              </template>
            </div>

            <!-- Badge detail view (when a badge is clicked) -->
            <div v-else key="badge" class="nge-profile-viz-panel nge-profile-viz-badge nge-profile-viz-badge--detail">
              <img
                :src="getBadgeUrl(selectedBadge.imageKey)"
                :alt="selectedBadge.name"
                class="nge-profile-viz-badge-icon nge-profile-viz-badge-icon--large"
                :class="`nge-badge--${selectedBadge.slug}`"
              />
              <div class="nge-profile-viz-badge-name">{{ selectedBadge.name }}</div>
              <div class="nge-profile-viz-badge-desc">{{ selectedBadge.description }}</div>
              <div class="nge-profile-viz-badge-threshold">
                Unlocked at <strong>{{ selectedBadge.threshold.toLocaleString() }}</strong> {{ thresholdLabel(selectedBadge) }}
              </div>
              <button class="nge-profile-viz-badge-back" @click="selectedBadge = null">
                ← {{ favoriteBadge || favoriteSpecialBadge ? 'Back to favorite' : 'Back' }}
              </button>
            </div>

          </Transition>

          <!-- Divider between badge and streak -->
          <div class="nge-profile-right-divider"></div>

          <!-- Weekly podium counts (🥇 × N etc.) — one row per metric -->
          <div v-if="anyPodium" class="nge-profile-section nge-profile-section--podium">
            <div class="nge-profile-section-label nge-profile-section-label--amber">▌ Top of the Week</div>

            <!-- Edits podium -->
            <div v-if="podiumTotal > 0" class="nge-profile-podium-metric">
              <div class="nge-profile-podium-metric-label">Edits</div>
              <div class="nge-profile-podium-row">
                <div v-if="podium.gold > 0" class="nge-profile-podium-tile nge-profile-podium-tile--gold" :title="`Finished 1st place in edits ${podium.gold} week${podium.gold === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥇</span>
                  <span class="nge-profile-podium-count">× {{ podium.gold }}</span>
                </div>
                <div v-if="podium.silver > 0" class="nge-profile-podium-tile nge-profile-podium-tile--silver" :title="`Finished 2nd place in edits ${podium.silver} week${podium.silver === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥈</span>
                  <span class="nge-profile-podium-count">× {{ podium.silver }}</span>
                </div>
                <div v-if="podium.bronze > 0" class="nge-profile-podium-tile nge-profile-podium-tile--bronze" :title="`Finished 3rd place in edits ${podium.bronze} week${podium.bronze === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥉</span>
                  <span class="nge-profile-podium-count">× {{ podium.bronze }}</span>
                </div>
              </div>
            </div>

            <!-- Completions podium -->
            <div v-if="podiumCompletionsTotal > 0" class="nge-profile-podium-metric">
              <div class="nge-profile-podium-metric-label">Cells</div>
              <div class="nge-profile-podium-row">
                <div v-if="podiumCompletions.gold > 0" class="nge-profile-podium-tile nge-profile-podium-tile--gold" :title="`Finished 1st place in cells ${podiumCompletions.gold} week${podiumCompletions.gold === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥇</span>
                  <span class="nge-profile-podium-count">× {{ podiumCompletions.gold }}</span>
                </div>
                <div v-if="podiumCompletions.silver > 0" class="nge-profile-podium-tile nge-profile-podium-tile--silver" :title="`Finished 2nd place in cells ${podiumCompletions.silver} week${podiumCompletions.silver === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥈</span>
                  <span class="nge-profile-podium-count">× {{ podiumCompletions.silver }}</span>
                </div>
                <div v-if="podiumCompletions.bronze > 0" class="nge-profile-podium-tile nge-profile-podium-tile--bronze" :title="`Finished 3rd place in cells ${podiumCompletions.bronze} week${podiumCompletions.bronze === 1 ? '' : 's'}`">
                  <span class="nge-profile-podium-medal">🥉</span>
                  <span class="nge-profile-podium-count">× {{ podiumCompletions.bronze }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Streak + Activity Chart -->
          <div class="nge-profile-section nge-profile-section--streak">
            <div class="nge-profile-section-label nge-profile-section-label--amber">▌ Streak</div>
            <div class="nge-profile-streak-row" v-if="profileStats.currentStreak > 0 || profileStats.longestStreak > 0">
              <div class="nge-profile-streak-current">
                <span class="nge-profile-streak-flame">🔥</span>
                <span class="nge-profile-streak-count">{{ profileStats.currentStreak }}</span>
                <span class="nge-profile-streak-unit">day{{ profileStats.currentStreak === 1 ? '' : 's' }} current</span>
              </div>
              <div class="nge-profile-streak-best" v-if="profileStats.longestStreak > 0">
                <span class="nge-profile-streak-best-label">Best</span>
                <span class="nge-profile-streak-best-val">{{ profileStats.longestStreak }}d</span>
              </div>
            </div>

            <!-- 14-day activity chart -->
            <div class="nge-profile-activity-chart">
              <div class="nge-profile-chart-label">Last 14 Days</div>
              <div class="nge-profile-chart-bars">
                <div
                  v-for="day in last14Days"
                  :key="day.date"
                  class="nge-profile-chart-col"
                  :title="`${day.date}: ${day.edits} edits, ${day.completions} completions`"
                >
                  <div class="nge-profile-chart-bar-wrap">
                    <div
                      v-if="day.completions > 0"
                      class="nge-profile-chart-bar nge-profile-chart-bar--completion"
                      :style="{ height: Math.max(2, (day.completions / chartMax) * 48) + 'px' }"
                    ></div>
                    <div
                      v-if="day.edits > 0"
                      class="nge-profile-chart-bar nge-profile-chart-bar--edit"
                      :style="{ height: Math.max(2, (day.edits / chartMax) * 48) + 'px' }"
                    ></div>
                  </div>
                  <div class="nge-profile-chart-day">{{ day.label }}</div>
                </div>
              </div>
              <div class="nge-profile-chart-legend">
                <span class="nge-profile-chart-legend-item">
                  <span class="nge-profile-chart-legend-dot nge-profile-chart-legend-dot--edit"></span> Edits
                </span>
                <span class="nge-profile-chart-legend-item">
                  <span class="nge-profile-chart-legend-dot nge-profile-chart-legend-dot--completion"></span> Completions
                </span>
              </div>
            </div>
          </div>

        </div><!-- end right column -->

      </div><!-- end .nge-profile-body (Overview) -->

      <!-- ── Trophy Case tab ─────────────────────────────────── -->
      <div v-else-if="activeTab === 'trophyCase'" class="nge-profile-body nge-profile-body--trophy">
        <div class="nge-trophy-scroll">

          <!-- ── Featured badge banner ── -->
          <div v-if="selectedSpecialBadge" class="nge-trophy-featured">
            <div class="nge-trophy-featured-effects">
              <div class="nge-trophy-ring nge-trophy-ring--1"></div>
              <div class="nge-trophy-ring nge-trophy-ring--2"></div>
              <div class="nge-trophy-ring nge-trophy-ring--3"></div>
              <div class="nge-trophy-ring nge-trophy-ring--4"></div>
              <div class="nge-trophy-ring nge-trophy-ring--5"></div>
              <span v-for="i in 12" :key="i" class="nge-trophy-orbit-dot" :style="{ '--j': i }"></span>
              <div class="nge-trophy-aura"></div>
            </div>
            <img
              :src="selectedSpecialBadge.badge?.image_url || selectedSpecialBadge.badge?.thumbnail_url"
              :alt="selectedSpecialBadge.badge?.name"
              class="nge-trophy-featured-icon nge-trophy-featured-icon--animated"
              @click="zoomBadge(selectedSpecialBadge.badge?.image_url || selectedSpecialBadge.badge?.thumbnail_url, selectedSpecialBadge.badge?.name, selectedSpecialBadge.badge?.description, 'Won on ' + formatDate(selectedSpecialBadge.awarded_at))"
            />
            <div class="nge-trophy-featured-info">
              <div class="nge-trophy-featured-name nge-trophy-featured-name--scifi">{{ selectedSpecialBadge.badge?.name || 'Award' }}</div>
              <div v-if="selectedSpecialBadge.badge?.description" class="nge-trophy-featured-desc">{{ selectedSpecialBadge.badge.description }}</div>
              <div class="nge-trophy-featured-desc" style="font-style: italic; opacity: 0.7;">Won for being awesome</div>
              <div class="nge-trophy-featured-threshold">
                Won on <strong>{{ formatDate(selectedSpecialBadge.awarded_at) }}</strong>
              </div>
              <div v-if="favoriteBadgeSlug === (selectedSpecialBadge.badge?.slug || `special-${selectedSpecialBadge.id}`)" class="nge-trophy-featured-fav-label">★ Your favorite badge</div>
            </div>
            <button
              class="nge-trophy-featured-star"
              :class="{ 'nge-trophy-featured-star--active': favoriteBadgeSlug === (selectedSpecialBadge.badge?.slug || `special-${selectedSpecialBadge.id}`) }"
              :title="favoriteBadgeSlug === (selectedSpecialBadge.badge?.slug || `special-${selectedSpecialBadge.id}`) ? 'Remove from favorites' : 'Set as favorite badge'"
              @click="toggleFavoriteSpecialBadge(selectedSpecialBadge)"
            >{{ favoriteBadgeSlug === (selectedSpecialBadge.badge?.slug || `special-${selectedSpecialBadge.id}`) ? '★' : '☆' }}</button>
          </div>
          <div v-else-if="favoriteSpecialBadge && !selectedBadge" class="nge-trophy-featured">
            <div class="nge-trophy-featured-effects">
              <div class="nge-trophy-ring nge-trophy-ring--1"></div>
              <div class="nge-trophy-ring nge-trophy-ring--2"></div>
              <div class="nge-trophy-ring nge-trophy-ring--3"></div>
              <div class="nge-trophy-ring nge-trophy-ring--4"></div>
              <div class="nge-trophy-ring nge-trophy-ring--5"></div>
              <span v-for="i in 12" :key="i" class="nge-trophy-orbit-dot" :style="{ '--j': i }"></span>
              <div class="nge-trophy-aura"></div>
            </div>
            <img
              :src="favoriteSpecialBadge.badge?.image_url || favoriteSpecialBadge.badge?.thumbnail_url"
              :alt="favoriteSpecialBadge.badge?.name"
              class="nge-trophy-featured-icon nge-trophy-featured-icon--animated"
              @click="zoomBadge(favoriteSpecialBadge.badge?.image_url || favoriteSpecialBadge.badge?.thumbnail_url, favoriteSpecialBadge.badge?.name, favoriteSpecialBadge.badge?.description, 'Won on ' + formatDate(favoriteSpecialBadge.awarded_at))"
            />
            <div class="nge-trophy-featured-info">
              <div class="nge-trophy-featured-name nge-trophy-featured-name--scifi">{{ favoriteSpecialBadge.badge?.name || 'Award' }}</div>
              <div v-if="favoriteSpecialBadge.badge?.description" class="nge-trophy-featured-desc">{{ favoriteSpecialBadge.badge.description }}</div>
              <div class="nge-trophy-featured-desc" style="font-style: italic; opacity: 0.7;">Won for being awesome</div>
              <div class="nge-trophy-featured-threshold">
                Won on <strong>{{ formatDate(favoriteSpecialBadge.awarded_at) }}</strong>
              </div>
              <div class="nge-trophy-featured-fav-label">★ Your favorite badge</div>
            </div>
            <button
              class="nge-trophy-featured-star nge-trophy-featured-star--active"
              title="Remove from favorites"
              @click="toggleFavoriteSpecialBadge(favoriteSpecialBadge)"
            >★</button>
          </div>
          <div v-else-if="featuredBadge" class="nge-trophy-featured">
            <div class="nge-trophy-featured-effects">
              <div class="nge-trophy-ring nge-trophy-ring--1"></div>
              <div class="nge-trophy-ring nge-trophy-ring--2"></div>
              <div class="nge-trophy-ring nge-trophy-ring--3"></div>
              <div class="nge-trophy-ring nge-trophy-ring--4"></div>
              <div class="nge-trophy-ring nge-trophy-ring--5"></div>
              <span v-for="i in 12" :key="i" class="nge-trophy-orbit-dot" :style="{ '--j': i }"></span>
              <div class="nge-trophy-aura"></div>
            </div>
            <img
              :src="getBadgeUrl(featuredBadge.imageKey)"
              :alt="featuredBadge.name"
              class="nge-trophy-featured-icon nge-trophy-featured-icon--animated"
              :class="`nge-badge--${featuredBadge.slug}`"
              @click="zoomBadge(getBadgeUrl(featuredBadge.imageKey), featuredBadge.name, featuredBadge.description, 'Unlocked at ' + featuredBadge.threshold.toLocaleString() + ' ' + thresholdLabel(featuredBadge))"
            />
            <div class="nge-trophy-featured-info">
              <div class="nge-trophy-featured-name nge-trophy-featured-name--scifi">{{ featuredBadge.name }}</div>
              <div class="nge-trophy-featured-desc">{{ featuredBadge.description }}</div>
              <div class="nge-trophy-featured-threshold">
                Unlocked at <strong>{{ featuredBadge.threshold.toLocaleString() }}</strong> {{ thresholdLabel(featuredBadge) }}
              </div>
              <div v-if="favoriteBadgeSlug === featuredBadge.slug" class="nge-trophy-featured-fav-label">★ Your favorite badge</div>
            </div>
            <button
              class="nge-trophy-featured-star"
              :class="{ 'nge-trophy-featured-star--active': favoriteBadgeSlug === featuredBadge.slug }"
              :title="favoriteBadgeSlug === featuredBadge.slug ? 'Remove from favorites' : 'Set as favorite badge'"
              @click="toggleFavoriteBadge(featuredBadge)"
            >{{ favoriteBadgeSlug === featuredBadge.slug ? '★' : '☆' }}</button>
          </div>

          <!-- Exploration track (Cell Achievements first) -->
          <div class="nge-trophy-track">
            <div class="nge-trophy-track-label" style="color: #90fff2;">Cell Achievements
              <span class="nge-trophy-track-count">{{ earnedExplorationBadges.earned.length }} earned</span>
            </div>
            <div class="nge-trophy-grid">
              <div
                v-for="badge in earnedExplorationBadges.earned"
                :key="badge.id"
                class="nge-trophy-badge nge-trophy-badge--exploration"
                :class="{
                  'nge-trophy-badge--selected': selectedBadge?.id === badge.id,
                  'nge-trophy-badge--favorited': favoriteBadgeSlug === badge.slug,
                }"
                @click="onBadgeClick(badge)"
              >
                <img :src="getBadgeUrl(badge.imageKey)" :alt="badge.name" class="nge-trophy-badge-icon" :class="`nge-badge--${badge.slug}`" />
                <div class="nge-trophy-badge-name">{{ badge.name }}</div>
                <div class="nge-trophy-badge-desc">{{ badge.description }}</div>
                <div class="nge-trophy-badge-threshold">{{ badge.threshold.toLocaleString() }} cells</div>
              </div>
            </div>
          </div>

          <!-- Proofreading track -->
          <div class="nge-trophy-track">
            <div class="nge-trophy-track-label" style="color: #ffd08a;">Proofreading Achievements
              <span class="nge-trophy-track-count">{{ earnedBuildingBadges.earned.length }} earned</span>
            </div>
            <div class="nge-trophy-grid">
              <div
                v-for="badge in earnedBuildingBadges.earned"
                :key="badge.id"
                class="nge-trophy-badge nge-trophy-badge--building"
                :class="{
                  'nge-trophy-badge--selected': selectedBadge?.id === badge.id,
                  'nge-trophy-badge--favorited': favoriteBadgeSlug === badge.slug,
                }"
                @click="onBadgeClick(badge)"
              >
                <img :src="getBadgeUrl(badge.imageKey)" :alt="badge.name" class="nge-trophy-badge-icon" :class="`nge-badge--${badge.slug}`" />
                <div class="nge-trophy-badge-name">{{ badge.name }}</div>
                <div class="nge-trophy-badge-desc">{{ badge.description }}</div>
                <div class="nge-trophy-badge-threshold">{{ badge.threshold.toLocaleString() }} edits</div>
              </div>
            </div>
          </div>

          <!-- Special Awards -->
          <div v-if="profileSpecialBadges.length > 0" class="nge-trophy-track">
            <div class="nge-trophy-track-label">★ Special Awards</div>
            <div class="nge-trophy-grid">
              <div
                v-for="award in profileSpecialBadges"
                :key="award.id"
                class="nge-trophy-badge"
                :class="{ 'nge-trophy-badge--selected': selectedSpecialBadge?.id === award.id }"
                :title="specialBadgeTooltip(award)"
                @click="onSpecialBadgeClick(award)"
              >
                <img :src="award.badge?.thumbnail_url || award.badge?.image_url" :alt="award.badge?.name" class="nge-trophy-badge-icon" />
                <div class="nge-trophy-badge-name">{{ award.badge?.name || 'Award' }}</div>
                <div v-if="award.badge?.description" class="nge-trophy-badge-desc">{{ award.badge.description }}</div>
              </div>
            </div>
          </div>

        </div>
      </div><!-- end Trophy Case -->

      <!-- ── Datasets tab: contributions per dataset + switcher ── -->
      <div v-if="activeTab === 'datasets'" class="nge-profile-body nge-profile-body--datasets">
        <div class="nge-ds-tab-intro">
          Your contributions across datasets. Click one to switch the viewer to it.
        </div>
        <div v-if="datasetStatsLoading && !Object.keys(datasetStats).length" class="nge-ds-tab-loading">
          Counting your edits…
        </div>
        <div class="nge-ds-tab-grid">
          <div
            v-for="ds in DATASETS"
            :key="ds.id"
            class="nge-ds-tab-card"
            :class="{
              'nge-ds-tab-card--active': canonicalDataset(segLayerName(ds)) === activeDatasetCanon,
              'nge-ds-tab-card--switching': switchingDatasetId === ds.id,
            }"
            @click="switchProfileDataset(ds)"
          >
            <div class="nge-ds-tab-card-head">
              <span class="nge-ds-tab-species">{{ SPECIES_ICONS[ds.species] }}</span>
              <span class="nge-ds-tab-label">{{ ds.label }}</span>
              <span v-if="canonicalDataset(segLayerName(ds)) === activeDatasetCanon" class="nge-ds-tab-badge">Active</span>
              <span v-else class="nge-ds-tab-switch">Switch ▸</span>
            </div>
            <div class="nge-ds-tab-desc">{{ ds.description }}</div>
            <div class="nge-ds-tab-stats">
              <template v-if="datasetContribution(ds)">
                <span class="nge-ds-tab-stat"><b>{{ (datasetContribution(ds)?.edits ?? 0).toLocaleString() }}</b> edits</span>
                <span class="nge-ds-tab-stat"><b>{{ (datasetContribution(ds)?.completions ?? 0).toLocaleString() }}</b> cells proofread</span>
                <span class="nge-ds-tab-stat"><b>{{ (datasetContribution(ds)?.helpRequests ?? 0).toLocaleString() }}</b> help requests</span>
                <span v-if="!hasContributed(ds)" class="nge-ds-tab-stat nge-ds-tab-stat--none">no contributions yet</span>
              </template>
              <span v-else class="nge-ds-tab-stat nge-ds-tab-stat--none">…</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Week in Science tab ───────────────────────────────── -->
      <div v-if="activeTab === 'weekInScience'" class="nge-profile-body nge-profile-body--week">
        <WeeklyRecapPanel embedded />
      </div>

      <!-- ── Admin Hub tab ─────────────────────────────────────── -->
      <div v-if="activeTab === 'adminHub'" class="nge-profile-body nge-profile-body--admin">
        <AdminHub :initial-sub-tab="adminInitialSubTab" />
      </div>

      <!-- ── Settings tab ──────────────────────────────────────── -->
      <div v-if="activeTab === 'settings'" class="nge-profile-body nge-profile-body--settings">
        <SettingsPanel embedded />
      </div>

      <!-- ── All Special Awards modal ── -->
      <div v-if="showAllSpecialModal" class="nge-special-modal-backdrop" @click.self="showAllSpecialModal = false">
        <div class="nge-special-modal">
          <div class="nge-special-modal-head">
            <span class="nge-special-modal-title">★ All Special Awards</span>
            <button class="nge-special-modal-close" @click="showAllSpecialModal = false">×</button>
          </div>
          <div class="nge-special-modal-grid">
            <div
              v-for="award in profileSpecialBadges"
              :key="award.id"
              class="nge-special-modal-tile"
              :title="specialBadgeTooltip(award)"
              @click="onSpecialBadgeClick(award); showAllSpecialModal = false;"
            >
              <img :src="award.badge?.thumbnail_url || award.badge?.image_url" :alt="award.badge?.name" class="nge-special-modal-icon" />
              <div class="nge-special-modal-name">{{ award.badge?.name || 'Award' }}</div>
              <div v-if="award.reason" class="nge-special-modal-reason">{{ award.reason }}</div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Mobile fullscreen badge viewer: tap the featured badge art to see
         it big, with a simplified award moment. Tap anywhere to close. -->
    <teleport to="body">
      <transition name="nge-bz">
        <div v-if="badgeZoom" class="nge-badge-zoom" @click="badgeZoom = null">
          <div class="nge-badge-zoom-halo"></div>
          <div class="nge-badge-zoom-ring"></div>
          <img :src="badgeZoom.img" :alt="badgeZoom.name" class="nge-badge-zoom-img" />
          <div class="nge-badge-zoom-name">{{ badgeZoom.name }}</div>
          <div v-if="badgeZoom.desc" class="nge-badge-zoom-desc">{{ badgeZoom.desc }}</div>
          <div v-if="badgeZoom.meta" class="nge-badge-zoom-meta">{{ badgeZoom.meta }}</div>
          <div class="nge-badge-zoom-hint">Tap anywhere to close</div>
        </div>
      </transition>
    </teleport>
  </modal-overlay>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&display=swap');
.nge-profile-modal { font-size: 0.9em; }

/* ─────────────────────────────────────────────────────────────────────────────
   OPEN ANIMATION — panel materializes fast, then sections stagger in
───────────────────────────────────────────────────────────────────────────── */

/* NOTE: NO position:relative here. Neuroglancer's .overlay-content already
   has position:absolute; top:50%; left:50%; transform:translate(-50%,-50%).
   Adding position:relative would override that and break centering.          */
.nge-profile-modal :deep(.nge-overlay) {
  overflow: hidden;
  animation: ngeProfileMaterialize 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
  background: linear-gradient(135deg, rgba(4, 6, 14, 0.97) 0%, rgba(8, 12, 24, 0.95) 50%, rgba(4, 8, 18, 0.97) 100%) !important;
}

/* ── Holographic edge glow — subtle orbiting light dots ── */
.nge-profile-modal :deep(.nge-overlay)::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  padding: 1px;
  background: conic-gradient(
    from var(--nge-holo-angle, 0deg),
    transparent 0%,
    rgba(74, 158, 255, 0.0) 10%,
    rgba(74, 158, 255, 0.35) 14%,
    rgba(0, 210, 255, 0.15) 18%,
    transparent 22%,
    transparent 35%,
    rgba(160, 120, 255, 0.25) 39%,
    rgba(120, 80, 220, 0.1) 43%,
    transparent 47%,
    transparent 60%,
    rgba(0, 255, 200, 0.2) 64%,
    rgba(74, 158, 255, 0.08) 68%,
    transparent 72%,
    transparent 85%,
    rgba(74, 158, 255, 0.15) 89%,
    transparent 93%
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: ngeHoloEdgeSpin 8s linear infinite;
  pointer-events: none;
  z-index: 1;
  opacity: 0.7;
}

@property --nge-holo-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@keyframes ngeHoloEdgeSpin {
  to { --nge-holo-angle: 360deg; }
}

/* Scanline removed — holographic border glow handled by ModalOverlay */

/* Close animation — overrides materialize with higher specificity */
.nge-profile-closing.nge-profile-modal :deep(.nge-overlay) {
  animation: ngeProfileCollapse 0.15s ease-in both;
}

@keyframes ngeProfileMaterialize {
  0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.97); filter: blur(10px) brightness(2.5); box-shadow: 0 0 80px rgba(0,180,255,0.5); }
  30%  { opacity: 0.8; transform: translate(-50%,-50%) scale(1.003); filter: blur(1px) brightness(1.2); }
  60%  { opacity: 1; transform: translate(-50%,-50%) scale(0.998); filter: blur(0) brightness(1.05); }
  100% { opacity: 1; transform: translate(-50%,-50%); filter: blur(0) brightness(1); box-shadow: none; }
}

@keyframes ngeProfileCollapse {
  0%   { opacity: 1; transform: translate(-50%,-50%) scale(1); filter: blur(0) brightness(1); }
  50%  { filter: blur(0) brightness(1.3); }
  100% { opacity: 0; transform: translate(-50%,-50%) scale(0.94); filter: blur(10px) brightness(2); }
}

/* (scanline keyframe removed — using ModalOverlay holographic effects) */

/* Staggered section roll-in */
@keyframes ngeSectionRollIn {
  0%   { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* ─────────────────────────────────────────────────────────────────────────────
   SHELL
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-shell {
  display: flex;
  flex-direction: column;
  /* Never taller than the viewport, whatever a tab's content does. The
     plain 90vh clamp still let content escape on short windows because
     ModalOverlay's overlay-content is overflow: visible; the hard cap plus
     overflow hidden means nothing can render past the shell edge, and each
     tab body scrolls internally. */
  max-height: min(90vh, calc(100vh - 32px));
  overflow: hidden;
  transition: width 0.25s ease;
}
.nge-profile-shell--trophy {
  width: 85vw;
  max-width: 1200px;
}
.nge-profile-shell--admin {
  width: 720px;
  max-width: 90vw;
}
/* Give Week in Science a real, consistent width instead of letting the shell
   shrink to the recap's old fixed content width (which read as a tiny box). */
.nge-profile-shell--week {
  width: 720px;
  max-width: 90vw;
}
.nge-profile-shell--datasets {
  width: 640px;
  max-width: 90vw;
}
.nge-profile-shell--settings {
  width: 520px;
  max-width: 90vw;
}
.nge-profile-body--settings {
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ── Datasets tab ── */
.nge-profile-body--datasets {
  flex-direction: column;
  overflow-y: auto;
  padding: 18px 22px 26px;
  gap: 12px;
}
.nge-ds-tab-intro {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}
.nge-ds-tab-loading {
  font-size: 12px;
  color: rgba(100, 200, 255, 0.7);
}
.nge-ds-tab-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.nge-ds-tab-card {
  position: relative;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.nge-ds-tab-card:hover:not(.nge-ds-tab-card--active) {
  background: rgba(100, 200, 255, 0.06);
  border-color: rgba(100, 200, 255, 0.2);
}
.nge-ds-tab-card--active {
  background: rgba(100, 200, 255, 0.1);
  border-color: rgba(100, 200, 255, 0.4);
  cursor: default;
}
.nge-ds-tab-card--switching {
  opacity: 0.5;
  pointer-events: none;
}
.nge-ds-tab-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.nge-ds-tab-species { font-size: 16px; line-height: 1; }
.nge-ds-tab-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  flex: 1;
}
.nge-ds-tab-badge {
  font-size: 10px;
  font-weight: 600;
  color: #64c8ff;
  background: rgba(100, 200, 255, 0.12);
  padding: 1px 7px;
  border-radius: 3px;
  letter-spacing: 0.03em;
}
.nge-ds-tab-switch {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.nge-ds-tab-card:hover .nge-ds-tab-switch { color: #64c8ff; }
.nge-ds-tab-desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  margin: 4px 0 8px 24px;
  line-height: 1.35;
}
.nge-ds-tab-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-left: 24px;
}
.nge-ds-tab-stat {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.6);
}
.nge-ds-tab-stat b {
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
}
.nge-ds-tab-stat--none {
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
}

/* ── Topbar ── */
.nge-profile-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px 8px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(74, 158, 255, 0.1);
  background: rgba(74, 158, 255, 0.04);
}

.nge-profile-topbar-label {
  font-size: 0.65em;
  letter-spacing: 0.18em;
  color: rgba(74, 158, 255, 0.55);
  text-transform: uppercase;
  font-weight: 600;
}
/* Whose profile this is, visible on every tab. */
.nge-profile-topbar-name {
  color: #f5d142;
  text-transform: none;
  letter-spacing: 0.1em;
}

.nge-profile-exit {
  background: none; border: none;
  color: #666; font-size: 1.5em; cursor: pointer; line-height: 1; padding: 0;
}
.nge-profile-exit:hover { color: #fff; }

/* ── Three-column body ── */
.nge-profile-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* ── Shared column base ── */
.nge-profile-col {
  overflow-y: auto;
  /* macOS hides overlay scrollbars until you scroll, which made a clipped
     column and a scrollable one look identical. A slim always-there thumb
     says "there's more". */
  scrollbar-gutter: stable;
  padding: 20px 24px 32px;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 158, 255, 0.2) rgba(255, 255, 255, 0.03);
}
.nge-profile-col::-webkit-scrollbar        { width: 4px; }
.nge-profile-col::-webkit-scrollbar-track  { background: rgba(255, 255, 255, 0.02); }
.nge-profile-col::-webkit-scrollbar-thumb  { background: rgba(74, 158, 255, 0.2); border-radius: 2px; }
.nge-profile-col::-webkit-scrollbar-thumb:hover { background: rgba(74, 158, 255, 0.4); }

/* Left: stats + recent cells */
.nge-profile-col--left {
  width: 460px;
  flex-shrink: 0;
}

/* Center: badges + streak + countdown */
.nge-profile-col--center {
  width: 460px;
  flex-shrink: 0;
  overflow-x: hidden;
  border-left: 1px solid rgba(74, 158, 255, 0.08);
  border-right: 1px solid rgba(74, 158, 255, 0.08);
  background: rgba(74, 158, 255, 0.01);
}

/* Right: latest badge / badge detail */
.nge-profile-col--right {
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: rgba(74, 158, 255, 0.015);
  /* Was overflow: hidden, which silently clipped Favorite Badge / Top of the
     Week on short windows while the sibling columns scrolled (the recurring
     "a rule was written but never applied" trap, in reverse: a rule applied
     that never should have been). */
  overflow-y: auto;
  overflow-x: hidden;
}

/* ─────────────────────────────────────────────────────────────────────────────
   HEADER
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-header {
  padding-bottom: 22px;
  margin-bottom: 6px;
  animation: ngeSectionRollIn 0.2s ease-out 0.12s both;
}

.nge-profile-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

/* Flag + inline picker */
.nge-profile-flag-wrap {
  position: relative;
  flex-shrink: 0;
}

/* Flag images (cross-platform — Windows doesn't render flag emoji) */
.nge-flag-img { width: 24px; height: 18px; object-fit: cover; border-radius: 2px; vertical-align: middle; }
.nge-flag-img.nge-pyr-icon { width: 22px; height: 22px; object-fit: contain; border-radius: 0; }
.nge-flag-img--picker { width: 28px; height: 20px; }

.nge-profile-flag {
  background: none; border: none;
  font-size: 1.55em; line-height: 1; cursor: pointer; padding: 2px;
  border-radius: 4px;
  transition: background 0.12s, transform 0.12s;
}
.nge-profile-flag:hover            { background: rgba(255,255,255,0.08); transform: scale(1.1); }
.nge-profile-flag--active          { background: rgba(74,158,255,0.15); }

.nge-profile-flag-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 200;
  background: #1c1e26;
  border: 1px solid rgba(74, 158, 255, 0.3);
  border-radius: 8px;
  padding: 6px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 158, 255, 0.3) transparent;
}

.nge-profile-flag-opt {
  background: none; border: none;
  font-size: 1.3em; padding: 4px; border-radius: 4px; cursor: pointer;
  transition: background 0.1s, transform 0.1s;
  line-height: 1;
}
.nge-profile-flag-opt:hover          { background: rgba(255,255,255,0.1); transform: scale(1.15); }
.nge-profile-flag-opt--active        { background: rgba(74,158,255,0.18); }

/* Picker transition */
.nge-flag-picker-enter-active  { transition: all 0.14s cubic-bezier(0.34, 1.56, 0.64, 1); }
.nge-flag-picker-leave-active  { transition: all 0.1s ease-in; }
.nge-flag-picker-enter-from,
.nge-flag-picker-leave-to      { opacity: 0; transform: translateY(-6px) scale(0.96); }

/* Name */
.nge-profile-name {
  font-size: 1.45em;
  font-weight: 700;
  color: #eef0f4;
  letter-spacing: -0.01em;
  text-shadow: 0 0 28px rgba(74, 158, 255, 0.28);
  flex: 1;
}

/* Edit button */
.nge-profile-edit-btn {
  background: none; border: none;
  color: rgba(74, 158, 255, 0.4);
  font-size: 1em; cursor: pointer; padding: 2px 4px; border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.nge-profile-edit-btn:hover {
  color: rgba(74, 158, 255, 0.9);
  background: rgba(74, 158, 255, 0.1);
}

/* The chat handle. Monospace and accented so it reads as an identifier rather
   than more grey metadata like the email below it. */
.nge-profile-username {
  font-family: 'Consolas', 'Monaco', monospace;
  color: #9db8ff;
  font-size: 0.86em;
  margin-bottom: 2px;
}

.nge-profile-email {
  font-style: italic;
  color: rgba(158, 158, 158, 0.7);
  font-size: 0.82em;
  margin-bottom: 4px;
}

.nge-profile-bio {
  font-size: 0.82em;
  color: #aaa;
  line-height: 1.45;
  font-style: italic;
  margin-top: 4px;
}

.nge-profile-bio-add {
  background: none; border: none;
  font-size: 0.78em;
  color: rgba(74, 158, 255, 0.45);
  cursor: pointer; padding: 0; margin-top: 4px;
  transition: color 0.15s;
  font-style: italic;
}
.nge-profile-bio-add:hover { color: rgba(74, 158, 255, 0.8); }

/* ─────────────────────────────────────────────────────────────────────────────
   SECTIONS (shared layout)
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-section {
  margin-bottom: 22px;
  padding-bottom: 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.nge-profile-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }

/* Section label: "▌ EDITS" style */
.nge-profile-section-label {
  font-size: 0.78em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  color: rgba(120, 180, 255, 0.95);
  margin-bottom: 12px;
}

.nge-profile-section-label--amber { color: rgba(255, 195, 110, 0.95); }

.nge-profile-section-label--green { color: rgba(160, 255, 175, 0.95); }

/* Section roll-in delays */
.nge-profile-section--edits     { animation: ngeSectionRollIn 0.2s ease-out 0.18s both; }
.nge-profile-section--cells     { animation: ngeSectionRollIn 0.2s ease-out 0.24s both; }
.nge-profile-section--streak    { animation: ngeSectionRollIn 0.2s ease-out 0.29s both; }
.nge-profile-section--badges    { animation: ngeSectionRollIn 0.2s ease-out 0.33s both; }
.nge-profile-section--countdown { animation: ngeSectionRollIn 0.2s ease-out 0.37s both; }

/* ── Achievement countdown ── */
.nge-profile-countdown-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.nge-profile-countdown-name { font-weight: 400; color: rgba(255,255,255,0.5); font-size: 0.82em; font-style: italic; }
.nge-profile-countdown-remaining { font-size: 0.86em; color: #cde; }
.nge-profile-countdown-track {
  height: 6px; background: rgba(255, 255, 255, 0.08); border-radius: 3px; overflow: hidden;
}
.nge-profile-countdown-fill {
  height: 100%; background: linear-gradient(90deg, #7f8, #4a9eff); border-radius: 3px;
  transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.nge-profile-countdown-labels {
  display: flex; justify-content: space-between; margin-top: 4px; font-size: 0.68em; color: #555;
}
.nge-profile-countdown-inline { margin-bottom: 10px; }
.nge-profile-countdown-fill--building { background: linear-gradient(90deg, #ffd08a, #f5a623); }
.nge-profile-countdown-fill--exploration { background: linear-gradient(90deg, #90fff2, #4ae5d5); }

/* ── Stat row ── */
.nge-profile-stat-row {
  display: flex;
  gap: 16px;
}

.nge-profile-stat-col {
  flex: 1;
}

.nge-profile-stat-label {
  font-size: 0.78em;
  color: #9bb;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  margin-bottom: 4px;
}

.nge-profile-stat-val {
  font-size: 1.2em;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  color: #d8dde8;
  letter-spacing: -0.02em;
}

.nge-profile-stat-val--hero {
  font-size: 1.6em;
  color: rgba(120, 200, 255, 0.9);
  text-shadow: 0 0 20px rgba(74, 158, 255, 0.3);
}

.nge-profile-stat-sub {
  font-size: 0.78em;
  color: #aab;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.nge-profile-stat-breakdown {
  display: flex;
  gap: 10px;
  margin-top: 5px;
}

.nge-profile-stat-bp {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.92em;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-variant-numeric: tabular-nums;
  color: #889;
}

.nge-profile-stat-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.nge-profile-stat-icon--merge { color: rgba(120, 200, 255, 0.9); }
.nge-profile-stat-icon--split { color: rgba(255, 160, 100, 0.9); }

/* ── Weekly Podium (🥇/🥈/🥉) ── */
.nge-profile-section--podium {
  margin-bottom: 14px;
}
.nge-profile-podium-metric {
  margin-top: 6px;
}
.nge-profile-podium-metric:first-of-type {
  margin-top: 0;
}
.nge-profile-podium-metric-label {
  font-size: 0.68em;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(180, 195, 215, 0.55);
  margin-bottom: 2px;
}
.nge-profile-podium-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.nge-profile-podium-tile {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  font-family: 'Orbitron', 'Rajdhani', sans-serif;
  font-size: 0.78em;
  font-weight: 600;
  letter-spacing: 0.06em;
}
.nge-profile-podium-medal {
  font-size: 1.25em;
  line-height: 1;
}
.nge-profile-podium-count {
  font-weight: 700;
}
.nge-profile-podium-tile--gold {
  background: linear-gradient(135deg, rgba(255, 215, 100, 0.14), rgba(255, 180, 60, 0.08));
  border: 1px solid rgba(255, 215, 100, 0.45);
  color: #ffd964;
  box-shadow: 0 0 10px rgba(255, 215, 100, 0.18);
}
.nge-profile-podium-tile--silver {
  background: linear-gradient(135deg, rgba(220, 230, 240, 0.12), rgba(180, 200, 220, 0.06));
  border: 1px solid rgba(220, 230, 240, 0.35);
  color: #dde6f0;
}
.nge-profile-podium-tile--bronze {
  background: linear-gradient(135deg, rgba(220, 140, 80, 0.13), rgba(180, 100, 50, 0.06));
  border: 1px solid rgba(220, 140, 80, 0.35);
  color: #e3a371;
}

/* ── Streak ── */
.nge-profile-streak-row {
  display: flex;
  align-items: baseline;
  gap: 18px;
  flex-wrap: wrap;
}

.nge-profile-streak-current {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.nge-profile-streak-flame {
  font-size: 1.25em; line-height: 1;
  animation: nge-streak-flicker 2s ease-in-out infinite;
}
.nge-profile-streak-count {
  font-size: 1.7em; font-weight: 800; color: #f5a623; line-height: 1;
  text-shadow: 0 0 8px rgba(245, 166, 35, 0.3);
  animation: nge-streak-glow 3s ease-in-out infinite;
}
@keyframes nge-streak-flicker {
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.1) rotate(-3deg); }
  75% { transform: scale(1.05) rotate(2deg); }
}
@keyframes nge-streak-glow {
  0%, 100% { text-shadow: 0 0 8px rgba(245, 166, 35, 0.3); }
  50% { text-shadow: 0 0 14px rgba(245, 166, 35, 0.5), 0 0 24px rgba(245, 166, 35, 0.2); }
}
.nge-profile-streak-unit  { font-size: 0.8em; color: #777; }

.nge-profile-streak-best {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 0.82em;
}
.nge-profile-streak-best-label { color: #9bb; }
.nge-profile-streak-best-val   { color: #cde; font-weight: 600; }

/* ── Activity chart ────────────────────────────────────────── */
.nge-profile-activity-chart {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.nge-profile-chart-label {
  font-size: 0.78em;
  color: #9bb;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-bottom: 6px;
}

.nge-profile-chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 64px;
  padding-bottom: 16px;
}

.nge-profile-chart-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.nge-profile-chart-bar-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 48px;
  width: 100%;
  gap: 1px;
}

.nge-profile-chart-bar {
  width: 100%;
  max-width: 12px;
  border-radius: 2px 2px 0 0;
  min-height: 0;
  transition: height 0.3s ease;
}

.nge-profile-chart-bar--edit {
  background: linear-gradient(to top, rgba(74, 158, 255, 0.5), rgba(74, 158, 255, 0.8));
}

.nge-profile-chart-bar--completion {
  background: linear-gradient(to top, rgba(255, 180, 50, 0.5), rgba(255, 180, 50, 0.85));
  border-radius: 2px;
}

.nge-profile-chart-day {
  font-size: 0.55em;
  color: #445;
  margin-top: 3px;
  white-space: nowrap;
}

.nge-profile-chart-legend {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 4px;
}

.nge-profile-chart-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62em;
  color: #556;
}

.nge-profile-chart-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.nge-profile-chart-legend-dot--edit {
  background: rgba(74, 158, 255, 0.7);
}

.nge-profile-chart-legend-dot--completion {
  background: rgba(255, 180, 50, 0.7);
}

/* ── Badges ── */
.nge-profile-badges-divider {
  height: 1px;
  margin: 14px 0 8px;
  background: linear-gradient(90deg, transparent 0%, rgba(74, 158, 255, 0.2) 30%, rgba(74, 158, 255, 0.2) 70%, transparent 100%);
}

.nge-profile-badges-hint {
  font-size: 0.7em;
  color: rgba(74, 158, 255, 0.45);
  font-style: italic;
  margin-bottom: 10px;
}

/* Badges sit smaller, five to a row, on faint shelf lines (Amy: "laid out
   as though they were set in 2 rows on a shelf"). The shelf is a repeating
   gradient tuned to the fixed row height plus the row gap. */
.nge-profile-badges-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px 8px;
  grid-auto-rows: 78px;
  padding: 0 2px;
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0px 74px,
    rgba(130, 190, 255, 0.30) 74px 75px,
    rgba(130, 190, 255, 0.08) 75px 77px,
    transparent 77px 90px
  );
}

.nge-profile-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: default;
  transition: transform 0.15s;
  border-radius: 6px;
  padding: 3px 2px;
  transition: transform 0.15s, background 0.15s;
}

.nge-profile-badge:not(.nge-profile-badge--locked) { cursor: pointer; }
.nge-profile-badge:not(.nge-profile-badge--locked):hover { transform: scale(1.1); }
/* Exploration badges: blue highlight (default) */
.nge-profile-badge--selected {
  background: rgba(74, 158, 255, 0.1);
  box-shadow: 0 0 0 1px rgba(74, 158, 255, 0.3) inset;
}
.nge-profile-badge--selected .nge-profile-badge-img {
  filter: drop-shadow(0 0 6px rgba(100, 180, 255, 0.75));
}
.nge-profile-badge--latest .nge-profile-badge-img {
  filter: drop-shadow(0 0 8px rgba(100, 180, 255, 0.6));
}
.nge-profile-badge--latest .nge-profile-badge-name {
  color: #8ac8ff;
}
/* Building badges: subtle golden highlight */
.nge-profile-badge--building.nge-profile-badge--selected {
  background: rgba(245, 166, 35, 0.06);
  box-shadow: 0 0 0 1px rgba(245, 166, 35, 0.2) inset;
}
.nge-profile-badge--building.nge-profile-badge--selected .nge-profile-badge-img {
  filter: drop-shadow(0 0 3px rgba(255, 200, 80, 0.35));
}
.nge-profile-badge--building.nge-profile-badge--latest .nge-profile-badge-img {
  filter: drop-shadow(0 0 4px rgba(255, 200, 80, 0.3));
}
.nge-profile-badge--building.nge-profile-badge--latest .nge-profile-badge-name {
  color: #ffd08a;
}
/* Twine: rotate 90° to look like an infinity symbol */
.nge-badge--twine { transform: rotate(90deg); }

/* ── "View All" tile in badge grid ── */
.nge-profile-badge--viewall {
  cursor: pointer;
  background: rgba(74, 158, 255, 0.04);
  border: 1px dashed rgba(74, 158, 255, 0.2);
  border-radius: 8px;
  transition: background 0.15s, border-color 0.15s, transform 0.15s;
}
.nge-profile-badge--viewall:hover {
  background: rgba(74, 158, 255, 0.1);
  border-color: rgba(74, 158, 255, 0.4);
  transform: scale(1.08);
}
.nge-profile-badge-viewall-icon {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  opacity: 0.7;
}

.nge-profile-section--special {
  border-top: none;
}

.nge-profile-badges-empty {
  font-size: 0.75em;
  color: #556;
  padding: 8px 0;
  font-style: italic;
}

.nge-profile-badges-toggle {
  background: none;
  border: 1px solid rgba(245, 166, 35, 0.25);
  color: rgba(255, 208, 138, 0.6);
  font-size: 0.72em;
  padding: 4px 12px;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 6px;
  transition: color 0.15s, border-color 0.15s;
}
.nge-profile-badges-toggle:hover {
  color: rgba(255, 208, 138, 0.9);
  border-color: rgba(245, 166, 35, 0.45);
}

/* Cells as highlighted tiles: each stat in its own softly lit card, even
   spacing, definitions on hover (the old sub-label row is gone). */
.nge-profile-stat-row--tiles {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.nge-profile-stat-tile {
  flex: 1 1 0;
  min-width: 86px;
  padding: 10px 12px;
  border-radius: 9px;
  background: rgba(74, 158, 255, 0.055);
  border: 1px solid rgba(74, 158, 255, 0.16);
  cursor: help;
  transition: background 0.15s, border-color 0.15s, transform 0.15s;
}
.nge-profile-stat-tile:hover {
  background: rgba(74, 158, 255, 0.1);
  border-color: rgba(74, 158, 255, 0.35);
  transform: translateY(-1px);
}

.nge-profile-right-divider {
  height: 1px;
  margin: 18px 20px;
  background: linear-gradient(90deg, transparent 0%, rgba(74, 158, 255, 0.15) 30%, rgba(74, 158, 255, 0.15) 70%, transparent 100%);
}

.nge-profile-badge-img {
  display: flex; align-items: center; justify-content: center;
  width: 46px; height: 46px;
  transition: filter 0.15s;
}

.nge-profile-badge-icon { width: 42px; height: 42px; object-fit: contain; }

.nge-profile-badge-mystery {
  width: 50px; height: 50px;
  background: rgba(255,255,255,0.04);
  border: 2px dashed rgba(255,255,255,0.12);
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
  display: flex; align-items: center; justify-content: center;
}
.nge-profile-badge-mystery-q {
  font-size: 1.1em; font-weight: 700; color: rgba(255,255,255,0.18); font-style: italic; line-height: 1;
}

.nge-profile-badge-name {
  font-size: 0.78em; color: #cde; margin-top: 3px; line-height: 1.2;
  max-width: 80px; word-break: break-word;
}
.nge-profile-badge-name--locked { color: #333; }

/* ─────────────────────────────────────────────────────────────────────────────
   VIZ COLUMN
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-viz-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  animation: ngeSectionRollIn 0.2s ease-out 0.15s both;
}

.nge-profile-viz-title {
  font-size: 0.78em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  color: rgba(74, 158, 255, 0.55);
  align-self: flex-start;
}

/* Badge detail in viz column */
.nge-profile-viz-badge { justify-content: flex-start; text-align: center; gap: 12px; padding-top: 16px; }

/* max-width guards: the right column is 360px, and anything wider walked
   off its right edge (Amy's clipped Favorite Badge report, 2026-08-17). */
.nge-profile-viz-badge-icon { width: 220px; height: 220px; max-width: 100%; object-fit: contain; filter: drop-shadow(0 0 20px rgba(74,158,255,0.4)); }
.nge-profile-viz-badge-icon--large { width: 240px; height: 240px; max-width: 100%; }
.nge-profile-viz-panel { max-width: 100%; box-sizing: border-box; }
.nge-profile-viz-badge-name,
.nge-profile-viz-badge-desc { max-width: 100%; overflow-wrap: break-word; padding: 0 10px; box-sizing: border-box; }

.nge-profile-viz-badge-name {
  font-family: 'Orbitron', 'Rajdhani', 'Audiowide', 'Share Tech Mono', ui-monospace, monospace;
  font-size: 1.1em; font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: linear-gradient(135deg, #e0ecff 0%, #58a6ff 50%, #a0d0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nge-profile-viz-badge-desc { font-size: 0.8em; color: #888; line-height: 1.4; font-style: italic; }

.nge-profile-viz-badge-threshold {
  font-size: 0.75em; color: #555; line-height: 1.5; text-align: center;
}
.nge-profile-viz-badge-threshold strong { color: rgba(74,158,255,0.7); font-size: 1.15em; }

.nge-profile-viz-badge-back {
  margin-top: 4px;
  background: none; border: none;
  font-size: 0.75em; color: rgba(74,158,255,0.5);
  cursor: pointer; padding: 4px 8px; border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.nge-profile-viz-badge-back:hover { color: rgba(74,158,255,0.9); background: rgba(74,158,255,0.1); }

/* Viz swap transition */
.nge-viz-swap-enter-active { transition: all 0.18s ease-out; }
.nge-viz-swap-leave-active { transition: all 0.12s ease-in; }
.nge-viz-swap-enter-from   { opacity: 0; transform: translateX(12px); }
.nge-viz-swap-leave-to     { opacity: 0; transform: translateX(-12px); }

/* ─────────────────────────────────────────────────────────────────────────────
   CELL HISTORY LIST
───────────────────────────────────────────────────────────────────────────── */
.nge-cell-list {
  margin-top: 16px;
}

.nge-cell-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.nge-cell-list-title {
  font-size: 0.82em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  color: rgba(74, 158, 255, 0.65);
  font-style: normal;
}

.nge-cell-list-columns {
  display: flex;
  align-items: center;
  padding: 0 2px 4px 42px; /* align with cell rows (past fav + pip) */
  border-bottom: 1px solid rgba(74, 158, 255, 0.08);
  margin-bottom: 2px;
}
.nge-cell-col-label {
  font-size: 0.78em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(180, 195, 220, 0.55);
}
.nge-cell-col-label--id { flex: 1; }
.nge-cell-col-label--type { width: 110px; text-align: left; }
.nge-cell-col-label--time { width: 64px; text-align: right; padding-right: 18px; }

.nge-cell-list-dataset {
  font-size: 0.62em !important;
  font-style: normal !important;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(74, 158, 255, 0.08);
  border: 1px solid rgba(74, 158, 255, 0.2);
  color: rgba(74, 158, 255, 0.6) !important;
  font-weight: 500;
}

.nge-cell-list-scroll {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 158, 255, 0.2) rgba(255, 255, 255, 0.03);
}

.nge-cell-list-scroll::-webkit-scrollbar       { width: 3px; }
.nge-cell-list-scroll::-webkit-scrollbar-track  { background: transparent; }
.nge-cell-list-scroll::-webkit-scrollbar-thumb  { background: rgba(74, 158, 255, 0.2); border-radius: 2px; }

.nge-cell-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 8px;
  background: none;
  border: none;
  border-radius: 4px;
  color: #cde;
  font-size: 0.95em;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  overflow: hidden;
}

.nge-cell-row:hover {
  background: rgba(74, 158, 255, 0.1);
  color: #fff;
}

.nge-cell-pip {
  flex-shrink: 0;
  width: 16px;
  text-align: center;
  font-size: 0.9em;
}

.nge-cell-status--complete .nge-cell-pip,
.nge-cell-pip.nge-cell-status--complete { color: #CE93D8; }
.nge-cell-status--typed .nge-cell-pip,
.nge-cell-pip.nge-cell-status--typed    { color: #4CAF50; }
.nge-cell-status--pending .nge-cell-pip,
.nge-cell-pip.nge-cell-status--pending  { color: #555; }

.nge-cell-id {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 0.95em;
  color: #9cf;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.nge-cell-type {
  flex: 1;
  font-size: 0.95em;
  color: #bcc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.nge-cell-time {
  flex-shrink: 0;
  font-size: 0.92em;
  color: #889;
  white-space: nowrap;
}

.nge-cell-fav {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85em;
  padding: 0 2px;
  opacity: 0.3;
  transition: opacity 0.12s;
}
.nge-cell-row:hover .nge-cell-fav { opacity: 0.6; }
.nge-cell-fav--active { opacity: 1 !important; color: #f5a623; }

.nge-cell-rename {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85em;
  padding: 0 2px;
  opacity: 0;
  color: rgba(74, 158, 255, 0.6);
  transition: opacity 0.12s;
}
.nge-cell-row:hover .nge-cell-rename { opacity: 0.6; }
.nge-cell-rename:hover { opacity: 1 !important; }

.nge-cell-empty {
  margin-top: 12px;
  font-size: 0.75em;
  color: #444;
  font-style: italic;
  line-height: 1.5;
}

/* ─────────────────────────────────────────────────────────────────────────────
   TAB BAR
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-tabbar {
  display: flex;
  gap: 2px;
  padding: 0 16px;
  border-bottom: 1px solid rgba(74, 158, 255, 0.1);
  flex-shrink: 0;
}

.nge-profile-tab {
  background: none;
  border: none;
  color: rgba(160, 175, 200, 0.6);
  font-size: 0.82em;
  font-weight: 600;
  padding: 8px 16px 7px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
  letter-spacing: 0.02em;
}
.nge-profile-tab:hover {
  color: rgba(200, 220, 255, 0.8);
}
.nge-profile-tab--active {
  color: rgba(200, 220, 255, 0.95);
  border-bottom-color: #4a9eff;
}

/* ─────────────────────────────────────────────────────────────────────────────
   TROPHY CASE TAB
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-body--trophy {
  display: block;
  padding: 0;
}

/* Week in Science tab — embedded recap, scrolls within the profile body. */
.nge-profile-body--week {
  display: block;
  padding: 0;
  max-height: calc(90vh - 100px);
  overflow-y: auto;
}

/* ─────────────────────────────────────────────────────────────────────────────
   ADMIN HUB TAB — fills the body, AdminHub.vue handles internal padding
───────────────────────────────────────────────────────────────────────────── */
.nge-profile-body--admin {
  display: block;
  padding: 0;
  max-height: calc(90vh - 100px);
  overflow: hidden;
}

.nge-trophy-scroll {
  overflow-y: auto;
  padding: 16px 24px 24px;
  max-height: calc(90vh - 100px);
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 158, 255, 0.2) rgba(255, 255, 255, 0.03);
}
.nge-trophy-scroll::-webkit-scrollbar        { width: 4px; }
.nge-trophy-scroll::-webkit-scrollbar-track  { background: rgba(255, 255, 255, 0.02); }
.nge-trophy-scroll::-webkit-scrollbar-thumb  { background: rgba(74, 158, 255, 0.2); border-radius: 2px; }
.nge-trophy-scroll::-webkit-scrollbar-thumb:hover { background: rgba(74, 158, 255, 0.4); }

.nge-trophy-track {
  margin-bottom: 24px;
}

.nge-trophy-track-label {
  font-size: 0.88em;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.nge-trophy-track-count {
  font-size: 0.82em;
  font-weight: 500;
  color: #667;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 2px 8px;
}

.nge-trophy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.nge-trophy-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px 6px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: transform 0.15s, background 0.15s, border-color 0.15s;
}
.nge-trophy-badge:hover {
  transform: scale(1.05);
  background: rgba(74, 158, 255, 0.06);
  border-color: rgba(74, 158, 255, 0.15);
}
.nge-trophy-badge--selected {
  background: rgba(74, 158, 255, 0.1) !important;
  border-color: rgba(74, 158, 255, 0.3) !important;
  box-shadow: 0 0 12px rgba(74, 158, 255, 0.15);
}
.nge-trophy-badge--building.nge-trophy-badge--selected {
  background: rgba(245, 166, 35, 0.08) !important;
  border-color: rgba(245, 166, 35, 0.25) !important;
  box-shadow: 0 0 12px rgba(245, 166, 35, 0.1);
}

.nge-trophy-badge--locked {
  opacity: 0.35;
  cursor: default;
}
.nge-trophy-badge--locked:hover {
  transform: none;
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.04);
}

.nge-trophy-badge-icon {
  width: 56px;
  height: 56px;
  object-fit: contain;
  margin-bottom: 6px;
  filter: drop-shadow(0 0 4px rgba(100, 180, 255, 0.2));
}

.nge-trophy-badge-mystery {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8em;
  color: #445;
  border: 2px dashed rgba(100, 120, 160, 0.2);
  border-radius: 50%;
  margin-bottom: 6px;
}

.nge-trophy-badge-name {
  font-size: 0.72em;
  font-weight: 600;
  color: rgba(200, 215, 240, 0.85);
  line-height: 1.2;
  margin-bottom: 2px;
}
.nge-trophy-badge-name--locked {
  color: #445;
}

.nge-trophy-badge-desc {
  font-size: 0.62em;
  color: #667;
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
}

.nge-trophy-badge-threshold {
  font-size: 0.6em;
  color: #556;
  margin-top: 3px;
  font-family: ui-monospace, 'Cascadia Code', monospace;
}

/* ── Featured badge banner (top of Trophy Case) ── */
.nge-trophy-featured {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px 28px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, rgba(8, 14, 28, 0.95), rgba(12, 22, 48, 0.9));
  border: 1px solid rgba(74, 158, 255, 0.15);
  border-radius: 14px;
  position: relative;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3), inset 0 0 40px rgba(74, 158, 255, 0.02);
}
.nge-trophy-featured-icon {
  width: 350px;
  height: 350px;
  object-fit: contain;
  flex-shrink: 0;
  filter: drop-shadow(0 0 20px rgba(100, 180, 255, 0.4));
}
.nge-trophy-featured-info {
  flex: 1;
  min-width: 0;
}
.nge-trophy-featured-name {
  font-size: 1.6em;
  font-weight: 700;
  color: #e0ecff;
  margin-bottom: 6px;
}
.nge-trophy-featured-desc {
  font-size: 1em;
  color: #99aabb;
  line-height: 1.5;
  margin-bottom: 8px;
}
.nge-trophy-featured-threshold {
  font-size: 0.88em;
  color: #667;
  font-family: ui-monospace, 'Cascadia Code', monospace;
}
.nge-trophy-featured-fav-label {
  font-size: 0.78em;
  color: rgba(74, 158, 255, 0.6);
  margin-top: 8px;
  letter-spacing: 0.03em;
}
.nge-trophy-featured-star {
  position: absolute;
  top: 16px;
  right: 18px;
  background: none;
  border: none;
  font-size: 2em;
  color: #556;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s, transform 0.2s;
  line-height: 1;
}
.nge-trophy-featured-star:hover {
  color: #58a6ff;
  transform: scale(1.15);
}
.nge-trophy-featured-star--active {
  color: #58a6ff;
  text-shadow: 0 0 12px rgba(88, 166, 255, 0.5);
}

/* Favorited badge gets subtle gold outline in grid */
.nge-trophy-badge--favorited {
  box-shadow: 0 0 0 1.5px rgba(255, 215, 0, 0.35) inset;
}
.nge-trophy-detail-close:hover { color: #f66; }

/* ── Hero-style effects around featured badge ── */
.nge-trophy-featured {
  overflow: visible;          /* let rings extend beyond banner */
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  gap: 72px;                  /* more space between icon and text */
}
.nge-trophy-featured-effects {
  position: absolute;
  top: 50%;
  left: 200px;               /* center on the 350px icon area + padding */
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 0;
}
.nge-trophy-featured-icon {
  z-index: 1;
  position: relative;
}
.nge-trophy-featured-icon--animated {
  animation: nge-trophy-float 5s ease-in-out infinite alternate;
}
.nge-trophy-featured-name--scifi {
  font-family: 'Orbitron', 'Rajdhani', 'Audiowide', 'Share Tech Mono', ui-monospace, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 1.8em;
  background: linear-gradient(135deg, #e0ecff 0%, #58a6ff 50%, #a0d0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
@keyframes nge-trophy-float {
  from { transform: translateY(0); }
  to   { transform: translateY(-8px); }
}

/* Rotating rings */
.nge-trophy-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid transparent;
  will-change: transform;
  transform: translateZ(0);
  top: 50%;
  left: 50%;
}
.nge-trophy-ring--1 {
  width: 380px; height: 380px;
  margin: -190px 0 0 -190px;
  border-color: rgba(0, 200, 255, 0.12);
  animation: nge-trophy-ring-spin 50s linear infinite;
  box-shadow: 0 0 12px rgba(0, 200, 255, 0.06);
}
.nge-trophy-ring--2 {
  width: 340px; height: 340px;
  margin: -170px 0 0 -170px;
  border-color: rgba(206, 147, 216, 0.08);
  border-style: dashed;
  animation: nge-trophy-ring-spin 70s linear infinite reverse;
  box-shadow: 0 0 12px rgba(206, 147, 216, 0.04);
}
.nge-trophy-ring--3 {
  width: 410px; height: 410px;
  margin: -205px 0 0 -205px;
  border-color: rgba(0, 255, 180, 0.05);
  border-width: 0.5px;
  animation: nge-trophy-ring-spin 90s linear infinite;
}
/* Trim-path style rings: dashed with animated dash offset */
.nge-trophy-ring--4 {
  width: 440px; height: 440px;
  margin: -220px 0 0 -220px;
  border: none;
  background: transparent;
  border-radius: 50%;
  /* SVG-like trim path via background gradient on a ring */
  box-shadow: none;
  outline: 1.5px dashed rgba(0, 200, 255, 0.07);
  outline-offset: -1px;
  animation: nge-trophy-ring-spin 120s linear infinite;
}
.nge-trophy-ring--5 {
  width: 360px; height: 360px;
  margin: -180px 0 0 -180px;
  border: 1.5px solid transparent;
  border-top-color: rgba(0, 200, 255, 0.1);
  border-right-color: rgba(206, 147, 216, 0.06);
  animation: nge-trophy-ring-spin 40s linear infinite reverse;
}
@keyframes nge-trophy-ring-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* Orbital dots */
.nge-trophy-orbit-dot {
  position: absolute;
  width: 4px; height: 4px;
  border-radius: 50%;
  background: rgba(0, 220, 255, 0.8);
  box-shadow: 0 0 6px rgba(0, 220, 255, 0.4), 0 0 12px rgba(0, 220, 255, 0.15);
  top: 50%; left: 50%;
  will-change: transform;
  animation: nge-trophy-orbit 40s linear infinite;
  animation-delay: calc(var(--j) * -3.3s);
}
.nge-trophy-orbit-dot:nth-child(odd) {
  width: 3px; height: 3px;
  background: rgba(206, 180, 255, 0.7);
  box-shadow: 0 0 5px rgba(206, 180, 255, 0.3);
  animation-duration: 55s;
  animation-direction: reverse;
}
.nge-trophy-orbit-dot:nth-child(3n) {
  width: 3.5px; height: 3.5px;
  background: rgba(0, 255, 180, 0.6);
  box-shadow: 0 0 5px rgba(0, 255, 180, 0.3);
  animation-duration: 48s;
}
/* Outer orbit particles */
.nge-trophy-orbit-dot:nth-child(n+7) {
  animation-name: nge-trophy-orbit-outer;
  animation-duration: 65s;
  width: 3px; height: 3px;
  opacity: 0.8;
}
.nge-trophy-orbit-dot:nth-child(8) {
  width: 3.5px; height: 3.5px;
  background: rgba(255, 200, 100, 0.6);
  box-shadow: 0 0 5px rgba(255, 200, 100, 0.3);
  animation-duration: 58s;
  animation-direction: reverse;
}
.nge-trophy-orbit-dot:nth-child(9) {
  width: 3px; height: 3px;
  background: rgba(100, 180, 255, 0.7);
  box-shadow: 0 0 5px rgba(100, 180, 255, 0.3);
  animation-duration: 72s;
}
.nge-trophy-orbit-dot:nth-child(10) {
  width: 3px; height: 3px;
  background: rgba(255, 150, 200, 0.6);
  box-shadow: 0 0 6px rgba(255, 150, 200, 0.3);
  animation-duration: 50s;
  animation-direction: reverse;
}
.nge-trophy-orbit-dot:nth-child(11) {
  width: 3.5px; height: 3.5px;
  background: rgba(0, 220, 255, 0.5);
  box-shadow: 0 0 5px rgba(0, 220, 255, 0.2);
  animation-duration: 80s;
}
.nge-trophy-orbit-dot:nth-child(12) {
  width: 2.5px; height: 2.5px;
  background: rgba(206, 147, 216, 0.6);
  box-shadow: 0 0 4px rgba(206, 147, 216, 0.3);
  animation-duration: 62s;
  animation-direction: reverse;
}
@keyframes nge-trophy-orbit {
  from { transform: rotate(calc(var(--j) * 45deg))       translateX(175px) rotate(calc(var(--j) * -45deg)); }
  to   { transform: rotate(calc(var(--j) * 45deg + 360deg)) translateX(175px) rotate(calc(var(--j) * -45deg - 360deg)); }
}
@keyframes nge-trophy-orbit-outer {
  from { transform: rotate(calc(var(--j) * 45deg))       translateX(210px) rotate(calc(var(--j) * -45deg)); }
  to   { transform: rotate(calc(var(--j) * 45deg + 360deg)) translateX(210px) rotate(calc(var(--j) * -45deg - 360deg)); }
}

/* Pulsing aura */
.nge-trophy-aura {
  position: absolute;
  width: 340px; height: 340px;
  top: 50%; left: 50%;
  margin: -170px 0 0 -170px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.05) 0%, rgba(206, 147, 216, 0.025) 40%, transparent 70%);
  animation: nge-trophy-aura-pulse 10s ease-in-out infinite;
  pointer-events: none;
}
@keyframes nge-trophy-aura-pulse {
  0%, 100% { transform: scale(1);    opacity: 0.5; }
  50%      { transform: scale(1.12); opacity: 0.85; }
}

/* ─────────────────────────────────────────────────────────────────────────────
   ALL SPECIAL AWARDS MODAL
───────────────────────────────────────────────────────────────────────────── */
.nge-special-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 4, 12, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ngeSectionRollIn 0.18s ease-out;
}
.nge-special-modal {
  width: min(720px, 92vw);
  max-height: 80vh;
  background: linear-gradient(135deg, rgba(8, 12, 24, 0.98), rgba(12, 18, 32, 0.97));
  border: 1px solid rgba(74, 158, 255, 0.25);
  border-radius: 12px;
  padding: 18px 20px 24px;
  box-shadow: 0 12px 48px rgba(0,0,0,0.55), 0 0 32px rgba(74, 158, 255, 0.08);
  display: flex;
  flex-direction: column;
}
.nge-special-modal-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
}
.nge-special-modal-title {
  font-size: 0.92em;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #cde;
  text-transform: uppercase;
}
.nge-special-modal-close {
  background: none; border: none;
  color: #889; font-size: 1.4em; cursor: pointer; line-height: 1;
}
.nge-special-modal-close:hover { color: #fff; }
.nge-special-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 14px;
  overflow-y: auto;
  /* Always reserve the scrollbar's space so revealing it on hover doesn't
     reflow the grid and resize every badge. */
  scrollbar-gutter: stable;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 158, 255, 0.2) transparent;
}
.nge-special-modal-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 8px;
  padding: 10px 6px;
  cursor: pointer;
  transition: transform 0.12s, background 0.12s, border-color 0.12s;
}
.nge-special-modal-tile:hover {
  transform: scale(1.05);
  background: rgba(74, 158, 255, 0.06);
  border-color: rgba(74, 158, 255, 0.18);
}
.nge-special-modal-icon {
  width: 56px; height: 56px; object-fit: contain;
  margin-bottom: 6px;
}
.nge-special-modal-name {
  font-size: 0.78em; color: #cde;
  font-weight: 600;
  line-height: 1.2;
}
.nge-special-modal-reason {
  font-size: 0.7em; color: #889;
  margin-top: 4px; line-height: 1.3;
  font-style: italic;
}

/* ─────────────────────────────────────────────────────────────────────────────
   MOBILE FULLSCREEN BADGE VIEWER
   Tap the featured badge art on a phone: the badge takes the whole screen
   with a simplified award moment (bloom halo, one sweeping ring, springy
   scale in). Tap anywhere to close.
───────────────────────────────────────────────────────────────────────────── */
.nge-badge-zoom {
  position: fixed;
  inset: 0;
  z-index: 10600;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  box-sizing: border-box;
  background: radial-gradient(ellipse at 50% 42%, rgba(20, 40, 80, 0.92), rgba(2, 5, 12, 0.97) 70%);
  backdrop-filter: blur(8px);
  text-align: center;
}
.nge-badge-zoom-halo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(90vw, 420px);
  height: min(90vw, 420px);
  transform: translate(-50%, -62%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(53, 181, 255, 0.28) 0%, rgba(53, 181, 255, 0.08) 45%, transparent 70%);
  animation: nge-bz-halo 2.4s ease-out both;
  pointer-events: none;
}
.nge-badge-zoom-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(74vw, 340px);
  height: min(74vw, 340px);
  transform: translate(-50%, -62%);
  border-radius: 50%;
  border: 1px solid rgba(53, 181, 255, 0.55);
  border-top-color: rgba(191, 233, 255, 0.9);
  box-shadow: 0 0 24px rgba(53, 181, 255, 0.35), inset 0 0 24px rgba(53, 181, 255, 0.12);
  animation: nge-bz-ring 2.2s cubic-bezier(0.22, 1, 0.36, 1) both;
  pointer-events: none;
}
.nge-badge-zoom-img {
  width: min(64vw, 300px);
  height: min(64vw, 300px);
  object-fit: contain;
  margin-bottom: 6px;
  filter: drop-shadow(0 0 24px rgba(100, 180, 255, 0.55));
  animation: nge-bz-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.nge-badge-zoom-name {
  font-family: 'Orbitron', sans-serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #f0f6ff;
  text-shadow: 0 0 14px rgba(53, 181, 255, 0.6);
  animation: nge-bz-rise 0.5s 0.15s ease-out both;
}
.nge-badge-zoom-desc {
  font-size: 14px;
  line-height: 1.5;
  color: #aebfdd;
  max-width: 320px;
  animation: nge-bz-rise 0.5s 0.25s ease-out both;
}
.nge-badge-zoom-meta {
  font-family: 'Orbitron', sans-serif;
  font-size: 11px;
  letter-spacing: 1.2px;
  color: rgba(53, 181, 255, 0.85);
  text-transform: uppercase;
  animation: nge-bz-rise 0.5s 0.35s ease-out both;
}
.nge-badge-zoom-hint {
  position: absolute;
  bottom: calc(18px + env(safe-area-inset-bottom));
  left: 0;
  right: 0;
  font-size: 11px;
  letter-spacing: 1px;
  color: rgba(143, 166, 204, 0.6);
  text-transform: uppercase;
  animation: nge-bz-rise 0.5s 0.6s ease-out both;
}

@keyframes nge-bz-pop {
  from { transform: scale(0.4); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
@keyframes nge-bz-rise {
  from { transform: translateY(10px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes nge-bz-halo {
  0%   { opacity: 0; transform: translate(-50%, -62%) scale(0.5); }
  35%  { opacity: 1; }
  100% { opacity: 0.75; transform: translate(-50%, -62%) scale(1); }
}
@keyframes nge-bz-ring {
  0%   { opacity: 0; transform: translate(-50%, -62%) scale(0.45) rotate(0deg); }
  30%  { opacity: 1; }
  100% { opacity: 0.7; transform: translate(-50%, -62%) scale(1) rotate(200deg); }
}

.nge-bz-enter-active, .nge-bz-leave-active { transition: opacity 0.25s ease; }
.nge-bz-enter-from, .nge-bz-leave-to { opacity: 0; }

/* The featured art is a tap target on phones. */
body.nge-mobile .nge-trophy-featured-icon { cursor: pointer; }

</style>
